<script setup>
import { ref, onMounted, computed, watch, nextTick, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';
import axios from '../axios';
import store from "../store";

const aiStatusPending = ref(false);
const aiPendingMessage = ref('');
const aiPendingCount = ref(0);

const checkAIStatus = async () => {
    try {
        const response = await axios.get("api/ai-extraction-status");
        if (response.data && response.data.status === 'pending') {
            aiStatusPending.value = true;
            aiPendingMessage.value = response.data.message || 'Due to AI failure, PDF extraction is currently pending.';
            aiPendingCount.value = response.data.pending_count || 0;
            
            /*
            let toastMessage = aiPendingMessage.value;
            if (aiPendingCount.value > 0) {
                toastMessage += ` (${aiPendingCount.value} PDF(s) in Queue)`;
            }
            // store.commit('setErrorNotification', toastMessage);
            */
        } else {
            aiStatusPending.value = false;
            aiPendingMessage.value = '';
            aiPendingCount.value = 0;
        }
    } catch (error) {
        console.error("Error checking AI status:", error);
    }
};

let statusInterval = null;

const bestBox = ref({ value: '', title: '' });
const packingResult = ref(null);
const route = useRoute();
const showModal = ref(false);
const showProceedModel = ref(false);
const errors = ref([]);
const isGetQuote = ref(true);
const fields = ref([{ type: '', size: '', confirm_size: '', weight: '20', subBox: null }]);
const fields_test = ref([{ type: '', size: '', confirm_size: '', weight: '20', subBox: null, selected_sizes: [] }]);
const hasClickedTestPrice = ref(false);
const addField_test = () => {
    fields_test.value.push({ type: '', size: '', confirm_size: '', weight: '20', subBox: null, selected_sizes: [] });
};
const insurance_per_box = ref(0);
// Helper function to calculate padded dimensions for sub-boxes (+4 inches total = 2 inches each side)
const calculatePaddedSize = (dimensions, isFragile = false, isUnframed = false, isStandard = false) => {
    if (!dimensions) return '';

    const PADDING = 4; // 4 inches total padding (2 inches on each side)

    const numbers = dimensions.match(/\d+(?:\.\d+)?/g);
    if (!numbers) return dimensions;

    let width, height, depth;

    if (numbers.length === 2) {
        width = parseFloat(numbers[0]);
        depth = isUnframed ? 6 : (isFragile ? 3 : 2);
        height = parseFloat(numbers[1]);
       
    } else if (numbers.length >= 3) {
        width = parseFloat(numbers[0]);
        depth = parseFloat(numbers[1]);
        height = parseFloat(numbers[2]);
    } else {
        width = parseFloat(numbers[0]);
        depth = isUnframed ? 6 : 2;
        height = parseFloat(numbers[0]);
    }

    const paddedWidth = Math.ceil(width + PADDING);
    const paddedHeight = Math.ceil(height + PADDING);
    
    let paddedDepth;
    if (isUnframed) {
        // Option A: Confirm test Box Size logic
        // 1. If depth <= 6 (or no depth) -> 6" depth picture box (empty space filled with cardboard & bubble wrap)
        // 2. If depth > 6 and <= 8 (e.g. 7") -> 8" depth picture box
        // 3. If depth > 8 -> depth + PADDING
        if (!depth || depth <= 6) {
            paddedDepth = 6;
        } else if (depth <= 8) {
            paddedDepth = 8;
        } else {
            paddedDepth = Math.ceil(depth + PADDING);
        }
    } else if (isStandard) {
        paddedDepth = Math.ceil(depth + PADDING); // Standard gets +4 padding on depth
    } else if (isFragile && Math.ceil(depth) === 2) {
        paddedDepth = 3; // Fragile gets depth rounded to 3 if it was 2
    } else {
        paddedDepth = Math.ceil(depth); 
    }

    return `${paddedWidth}x${paddedDepth}x${paddedHeight}`;
};



// Calculate main box size to fit all sub-boxes (with 2" padding on EACH side = 4" total per dimension)
// Add this function to find telescoped box solution

// Helper function to find closest matching predefined box size
const evaluateBoxFit = (itemDimsArray, boxDimsArray) => {
    // 1. Strict Rotation: No rotation allowed as per user request. Width to Width, Depth to Depth, Height to Height.
    // itemDimsArray and boxDimsArray are passed as [Length, Depth, Height]
    const iL = itemDimsArray[0];
    const iD = itemDimsArray[1];
    const iH = itemDimsArray[2];

    const bL = boxDimsArray[0];
    const bD = boxDimsArray[1];
    const bH = boxDimsArray[2];

    // Check if we can fit it exactly as is
    const uprightFits = iH <= bH && iL <= bL && iD <= bD;
    
    const sidewaysFits = false; // No rotation allowed

    if (!uprightFits && !sidewaysFits) {
        return { isValid: false, isFallbackValid: false, volume: Infinity };
    }

    // Determine effective dimensions for waste calculation
    let effL = iL, effH = iH;
    
    // If it fits both ways, choose the one with less height waste
    if (uprightFits && sidewaysFits) {
        const wasteUpright = bH - iH;
        const wasteSideways = bH - iL;
        if (wasteSideways < wasteUpright && wasteSideways >= 0) {
            effL = iH;
            effH = iL;
        }
    } else if (sidewaysFits && !uprightFits) {
        effL = iH;
        effH = iL;
    }

    // Calculate waste using effective Length and Height
    let waste0, waste1;
    if (effL <= bL && iD <= bD && effL <= bD && iD <= bL) {
         const w1 = ((bL - effL) / effL) * 100;
         const w2 = ((bD - effL) / effL) * 100;
         if (w1 < w2) {
             waste0 = w1;
             waste1 = ((bD - iD) / iD) * 100;
         } else {
             waste0 = w2;
             waste1 = ((bL - iD) / iD) * 100;
         }
    } else if (effL <= bL && iD <= bD) {
        waste0 = ((bL - effL) / effL) * 100;
        waste1 = ((bD - iD) / iD) * 100;
    } else {
        waste0 = ((bD - effL) / effL) * 100;
        waste1 = ((bL - iD) / iD) * 100;
    }
    const waste2 = ((bH - effH) / effH) * 100;

    const hasHighWaste = waste0 > 125 || waste1 > 125 || waste2 > 125;

    const volume = bL * bD * bH;
    const depthDiff = Math.abs(bD - iD);

    const sortedBox = [...boxDimsArray].sort((a, b) => a - b);
    const boxBalance = sortedBox[2] / sortedBox[0];
    const balancePenalty = Math.sqrt(boxBalance);
    const score = volume * balancePenalty;

    return { 
        isValid: !hasHighWaste, // True if strictly passes the 125% rule
        isFallbackValid: true,  // True because it physically fits
        volume,
        depthDiff,
        score
    };
};

const filteredBoxSizesForField = (fieldOrSubBox) => {
    if (fieldOrSubBox && fieldOrSubBox.isPicture) {
        const pictureBoxes = ["24x6x18", "24x18x6", "30x24x6", "30x6x24", "36x8x30", "36x6x42", "40x8x50", "54x8x28", "48x6x32", "42x8x60"];
        return BoxSize.value.filter(b => pictureBoxes.includes(b.value));
    }
    return BoxSize.value.slice(1);
};

const findClosestBoxSize = (paddedSize, isPicture = false, strict = false, enforceWasteLimit = false) => {
    if (!paddedSize) return '';

    const numbers = paddedSize.toLowerCase().split('x').map(Number);
    if (numbers.length !== 3) return paddedSize;

    let bestMatch = null;
    let bestVolume = Infinity;
    
    let fallbackMatch = null;
    let fallbackVolume = Infinity;

    const pictureBoxes = ["24x6x18", "24x18x6", "30x24x6", "30x6x24", "36x8x30", "36x6x42", "40x8x50", "54x8x28", "48x6x32", "42x8x60"];

    for (const box of BoxSize.value) {
        if (!box.value) continue;
        
        // If it's a picture, restrict to picture boxes only
        if (isPicture && !pictureBoxes.includes(box.value)) continue;

        const boxNumbers = box.value.toLowerCase().split('x').map(Number);
        if (boxNumbers.length !== 3) continue;

        const reqL = numbers[0];
        const reqD = numbers[1]; // Depth
        const reqH = numbers[2]; // Height

        // ✅ VALIDATION
        // NO ROTATION ALLOWED
        const isFit = (boxNumbers[0] >= reqL && boxNumbers[1] >= reqD && boxNumbers[2] >= reqH);

        const volume = boxNumbers[0] * boxNumbers[1] * boxNumbers[2];

        if (isFit) {
            // ✅ choose smallest valid box
            if (enforceWasteLimit) {
                const reqVol = reqL * reqD * reqH;
                if (volume > reqVol * 1.20) continue; // Reject if waste > 20%
            }
            if (volume < bestVolume) {
                bestVolume = volume;
                bestMatch = box.value;
            }
        } else {
            // Fallback: If depth exceeds available boxes, find best fit for Length and Height (No Rotation)
            const isFallbackFit = (boxNumbers[0] >= reqL && boxNumbers[2] >= reqH);
            if (isFallbackFit && volume < fallbackVolume) {
                fallbackVolume = volume;
                fallbackMatch = box.value;
            }
        }
    }

    return bestMatch || (strict ? '' : fallbackMatch) || '';
};
// Helper function to get all rotations of a box
// Helper function to get all rotations of a box (KEEP THIS ONE)
const getBoxRotations = (box) => {
    const { l, w, h } = box;
    return [
        { l, w, h },
        { l, h, w },
        { w, l, h },
        { w, h, l },
        { h, l, w },
        { h, w, l }
    ];
};
// Add this helper function to check if telescoping is needed
const needsTelescoping = (paddedDims, availableBoxes) => {
    if (!paddedDims) return false;

    // Check if any single box can fit the padded dimensions
    for (const box of availableBoxes) {
        if (!box.value) continue;
        const boxDims = parseBoxSize(box.value);
        if (!boxDims) continue;

        // NO ROTATION ALLOWED
        if (boxDims.l >= paddedDims.l && boxDims.w >= paddedDims.w && boxDims.h >= paddedDims.h) {
            const boxVol = boxDims.l * boxDims.w * boxDims.h;
            const reqVol = paddedDims.l * paddedDims.w * paddedDims.h;
            if (boxVol <= reqVol * 1.20) {
                return false; // Fits within 20% waste, no telescoping needed
            }
        }
    }
    return true; // No single box fits, need telescoping
};

// Updated function to find telescoped box solution (KEEP THIS)
const findTelescopedBox = (item, availableBoxes) => {
    if (!item) return null;
    const OVERLAP = 1; // 1 inch overlap for telescoping
    let bestMatch = null;
    let bestScore = Infinity;

    for (const box of availableBoxes) {
        if (!box.value) continue;
        const boxDims = parseBoxSize(box.value);
        if (!boxDims) continue;

        for (let numBoxes = 2; numBoxes <= 3; numBoxes++) {
            const combinedL = boxDims.l;
            const combinedH = boxDims.h;
            const combinedW = (boxDims.w * numBoxes) - ((numBoxes - 1) * OVERLAP);

            const itemDims = [item.l, item.h, item.w];
            const boxDimsArray = [combinedL, combinedH, combinedW];
            
            const fits = boxDimsArray[0] >= itemDims[0] &&
                         boxDimsArray[1] >= itemDims[1] &&
                         boxDimsArray[2] >= itemDims[2];

            if (fits) {
                const diff0 = Math.abs(boxDimsArray[0] - itemDims[0]);
                const diff1 = Math.abs(boxDimsArray[1] - itemDims[1]);
                const diff2 = Math.abs(boxDimsArray[2] - itemDims[2]);
                const score = (diff2 * 100000) + (diff1 * 1000) + diff0;
                
                if (score < bestScore) {
                    bestScore = score;
                    bestMatch = {
                        value: `${Math.ceil(combinedL)}x${Math.ceil(combinedH)}x${Math.ceil(combinedW)}`,
                        originalBox: box.value,
                        originalBoxTitle: box.title,
                        title: `Telescoped ${box.title}`,
                        is_telescoped: true,
                        numBoxes: numBoxes
                    };
                }
            }
        }
    }
    return bestMatch;
};

// Updated addSubBox_test function with telescoping logic


// Update the template to handle telescoping display
// Add this section inside your sub-boxes display where you show the size selection
// Calculate main box size to fit all sub-boxes (with 2" padding on EACH side = 4" total per dimension)
// KEEP THIS CORRECT VERSION
const calculateMainBoxSize = (subBoxes) => {
    if (!subBoxes || subBoxes.length === 0) return null;

    // Do not add extra padding to the aggregated bounding box,
    // as the individual sub-boxes/items already have their padding applied.
    const TOTAL_PADDING_PER_DIMENSION = 0; 


    // Get dimensions of each sub-box
    const subBoxDims = subBoxes.map(subBox => {
        if (subBox.is_telescoped && subBox.telescoped_combined) {
            const parsed = parseBoxSize(subBox.telescoped_combined);
            if (parsed) return parsed;
        }
        if (subBox.size) {
            const parsed = parseBoxSize(subBox.size);
            if (parsed) return parsed;
        }
        if (subBox.paddedSize) {
            const parsed = parseBoxSize(subBox.paddedSize);
            if (parsed) return parsed;
        }
        return { l: 0, w: 0, h: 0 };
    });

    // Get all possible rotations for each box
    const allRotations = subBoxDims.map(box => getBoxRotations(box));

    let bestResult = null;
    let minVolume = Infinity;

    // Try all rotation combinations recursively
    const tryCombinations = (index, selectedRotations) => {
        if (index === allRotations.length) {
            // Arrangement 1: Side by side (length-wise X)
            const totalLength = selectedRotations.reduce((sum, dim) => sum + dim.l, 0);
            const maxWidth1 = Math.max(...selectedRotations.map(dim => dim.w));
            const maxHeight1 = Math.max(...selectedRotations.map(dim => dim.h));

            const sideBySideX = {
                l: totalLength + TOTAL_PADDING_PER_DIMENSION,
                w: maxWidth1 + TOTAL_PADDING_PER_DIMENSION,
                h: maxHeight1 + TOTAL_PADDING_PER_DIMENSION,
                volume: (totalLength + TOTAL_PADDING_PER_DIMENSION) *
                    (maxWidth1 + TOTAL_PADDING_PER_DIMENSION) *
                    (maxHeight1 + TOTAL_PADDING_PER_DIMENSION)
            };

            // Arrangement 2: Stacked (height-wise Z)
            const maxLength2 = Math.max(...selectedRotations.map(dim => dim.l));
            const maxWidth2 = Math.max(...selectedRotations.map(dim => dim.w));
            const totalHeight = selectedRotations.reduce((sum, dim) => sum + dim.h, 0);

            const stackedZ = {
                l: maxLength2 + TOTAL_PADDING_PER_DIMENSION,
                w: maxWidth2 + TOTAL_PADDING_PER_DIMENSION,
                h: totalHeight + TOTAL_PADDING_PER_DIMENSION,
                volume: (maxLength2 + TOTAL_PADDING_PER_DIMENSION) *
                    (maxWidth2 + TOTAL_PADDING_PER_DIMENSION) *
                    (totalHeight + TOTAL_PADDING_PER_DIMENSION)
            };

            // Arrangement 3: Side by side (width-wise Y)
            const maxLength3 = Math.max(...selectedRotations.map(dim => dim.l));
            const totalWidth = selectedRotations.reduce((sum, dim) => sum + dim.w, 0);
            const maxHeight3 = Math.max(...selectedRotations.map(dim => dim.h));

            const sideBySideY = {
                l: maxLength3 + TOTAL_PADDING_PER_DIMENSION,
                w: totalWidth + TOTAL_PADDING_PER_DIMENSION,
                h: maxHeight3 + TOTAL_PADDING_PER_DIMENSION,
                volume: (maxLength3 + TOTAL_PADDING_PER_DIMENSION) *
                    (totalWidth + TOTAL_PADDING_PER_DIMENSION) *
                    (maxHeight3 + TOTAL_PADDING_PER_DIMENSION)
            };

            // Check all arrangements
            if (sideBySideX.volume < minVolume) {
                minVolume = sideBySideX.volume;
                bestResult = sideBySideX;
            }
            if (stackedZ.volume < minVolume) {
                minVolume = stackedZ.volume;
                bestResult = stackedZ;
            }
            if (sideBySideY.volume < minVolume) {
                minVolume = sideBySideY.volume;
                bestResult = sideBySideY;
            }
            return;
        }

        // Try each rotation for current box
        for (const rotation of allRotations[index]) {
            tryCombinations(index + 1, [...selectedRotations, rotation]);
        }
    };

    tryCombinations(0, []);

    // Raw Physical Stacking Heuristic (User's Real-Life Stacking Logic):
    // If original unpadded sizes are available, check if smaller items can physically sit flat 
    // on top of the largest base item without exceeding its surface area.
    const rawDims = subBoxes.map(sb => sb.originalSize ? parseBoxSize(sb.originalSize) : null).filter(Boolean);
    if (rawDims.length === subBoxes.length && rawDims.length > 1) {
        // Sort items by base surface area / max footprint descending
        const sortedRaw = [...rawDims].sort((a, b) => {
            const maxA = Math.max(a.l, a.w) * Math.max(Math.min(a.l, a.w), a.h);
            const maxB = Math.max(b.l, b.w) * Math.max(Math.min(b.l, b.w), b.h);
            return maxB - maxA;
        });

        const baseItem = sortedRaw[0];
        const remainingItems = sortedRaw.slice(1);

        // Check if remaining items can sit side-by-side on the base item's top surface.
        // If they lie flat, their height footprint contribution is their smallest dimension (e.g. depth=3).
        const maxRemainingHeight = Math.max(...remainingItems.map(item => Math.min(item.l, item.w, item.h)));
        
        // Padded stack footprint (+4 inches total padding applied directly to the final raw stack bounds)
        const paddedStackL = Math.ceil(Math.max(baseItem.l, baseItem.w));
        const paddedStackW = Math.ceil(Math.min(baseItem.l, baseItem.w) + 4);
        const paddedStackH = Math.ceil(baseItem.h + maxRemainingHeight + 4);
        const stackVol = paddedStackL * paddedStackW * paddedStackH;

        if (stackVol < minVolume) {
            minVolume = stackVol;
            bestResult = { l: paddedStackL, w: paddedStackW, h: paddedStackH };
            console.log(`🧱 Raw Physical Stack applied: Base(${baseItem.l}x${baseItem.w}x${baseItem.h}) + StackH(${maxRemainingHeight}) -> Padded Bounds: ${paddedStackL}x${paddedStackW}x${paddedStackH}`);
        }
    }

    // Optimal 3D Packing Heuristic:
    // If we can find a predefined box that can hold the total volume of all items,
    // and every individual item can fit inside it (via rotation),
    // and its volume is smaller than the linear stacking bounds, we use it!
    const totalItemVolume = subBoxDims.reduce((sum, item) => sum + (item.l * item.w * item.h), 0);
    
    if (BoxSize.value && Array.isArray(BoxSize.value)) {
        for (const box of BoxSize.value) {
            if (!box.value) continue;
            const numbers = box.value.split('x').map(Number);
            if (numbers.length !== 3) continue;
            
            const [boxL, boxW, boxH] = numbers;
            const boxVol = boxL * boxW * boxH;
            
            // 1. Check volume capacity
            if (totalItemVolume <= boxVol) {
                // 2. Check if every individual item fits in this box (considering rotation)
                let allItemsFit = true;
                for (const item of subBoxDims) {
                    const itemRots = getBoxRotations(item);
                    const itemFits = itemRots.some(rot => rot.l <= boxL && rot.w <= boxW && rot.h <= boxH);
                    if (!itemFits) {
                        allItemsFit = false;
                        break;
                    }
                }
                
                // If it fits geometrically and is smaller than our current best packing, use it!
                if (allItemsFit && boxVol < minVolume) {
                    minVolume = boxVol;
                    // Use parseBoxSize convention: l=Width(1st), h=Depth(2nd), w=Height(3rd)
                    bestResult = { l: boxL, h: boxW, w: boxH };
                }
            }
        }
    }

    if (bestResult) {
        bestResult.formatted = `${bestResult.l}x${bestResult.h}x${bestResult.w}`;
        console.log(`📦 Best main box: ${bestResult.formatted} (Volume: ${minVolume})`);
        return bestResult;
    }

    return null;
};
const addSubBox_test = (index) => {
    const field = fields_test.value[index];
    
    // Preserve original outer box values before sub-box calculations override them
    if (!field.original_main_size) {
        field.original_main_size = field.size;
        field.original_confirm_size = field.confirm_size;
        field.original_type = field.type;
        field.original_selected_sizes = Array.isArray(field.selected_sizes) ? [...field.selected_sizes] : [];
        field.original_is_telescoped = field.is_telescoped || false;
    }

    // Capture underlying Box test size value to initialize sub-box dropdown, and preserve the original item dimensions
    const activeOuterSize = field.size || field.confirm_size;
    const itemDimensionsTarget = field.original_confirm_size || field.confirm_size || field.size;
    const items = qoutationItems.value;

    if (activeOuterSize) {
        const isTelescopedOuter = field.is_telescoped || false;
        const outerSelectedSizes = Array.isArray(field.selected_sizes) ? [...field.selected_sizes] : [activeOuterSize];
        field.subBoxes = [{
            type: field.type || 'Custom',
            size: field.size || activeOuterSize,
            originalSize: (items && items.length > 0 && items[0].dimensions) ? items[0].dimensions : itemDimensionsTarget,
            originalItemDims: itemDimensionsTarget,
            weight: field.weight || '20',
            itemName: 'Assigned Sub-Box',
            is_telescoped: isTelescopedOuter,
            telescoped_combined: field.telescoped_combined || field.confirm_size || '',
            telescoped_boxes: outerSelectedSizes,
            selected_sizes: outerSelectedSizes,
            isManualChoice: true
        }];
    } else if (items && items.length > 0) {
        const subBoxesArray = [];

        items.forEach((item, idx) => {
            if (item.dimensions) {
                const itemNameLower = (item.item_name || item.name || "").toLowerCase();
                const notesLower = (item.notes || item.item_notes || item.description || item.ItemNotes || "").toLowerCase();
                const categoryLower = (item.category_name || item.category || item.item_category || item.type || item.item_type || "").toLowerCase();
                
                const isUnframedPicture = itemNameLower.includes("unframe") || notesLower.includes("unframe") || categoryLower.includes("unframe");
                
                if (isUnframedPicture && item.is_duplicate_display) {
                    return;
                }

                let paddedSize = '';
                let paddedDims = null;
                let isPictureItem = false;

                if (isUnframedPicture) {
                    const qty = parseInt(item.quantity) || 1;
                    const value = item.dimensions.toLowerCase().replace(/×/g, 'x').replace(/\s+/g, ' ').trim();
                    const wMatch = value.match(/(\d+(?:\.\d+)?)\s*w/i);
                    const hMatch = value.match(/(\d+(?:\.\d+)?)\s*h/i);
                    const dMatch = value.match(/(\d+(?:\.\d+)?)\s*d/i);
                    const lMatch = value.match(/(\d+(?:\.\d+)?)\s*l/i);
                    let width = wMatch ? parseFloat(wMatch[1]) : null;
                    let height = hMatch ? parseFloat(hMatch[1]) : null;
                    let parsedDepth = dMatch ? parseFloat(dMatch[1]) : null;
                    if (lMatch && width === null) width = parseFloat(lMatch[1]);
                    
                    const numbers = value.match(/\d+(?:\.\d+)?/g);
                    if (width === null && height === null && numbers) {
                        if (numbers.length === 2) {
                            width = parseFloat(numbers[0]);
                            height = parseFloat(numbers[1]);
                        } else if (numbers.length >= 3) {
                            width = parseFloat(numbers[0]);
                            parsedDepth = parseFloat(numbers[1]);
                            height = parseFloat(numbers[2]);
                        } else if (numbers.length === 1) {
                            width = parseFloat(numbers[0]);
                            height = parseFloat(numbers[0]);
                        }
                    }
                    if (width === null) width = 3;
                    if (height === null) height = 3;
                    
                    // Option A: Confirm test Box Size logic
                    const rawDepth = parsedDepth !== null ? parsedDepth : 6;
                    const totalD = qty > 1 ? (rawDepth + (qty - 1) * 0.5) : rawDepth;
                    
                    const paddedW = Math.ceil(width + 4);
                    const paddedH = Math.ceil(height + 4);
                    let paddedD = 6;
                    if (totalD <= 6) {
                        paddedD = 6;
                    } else if (totalD <= 8) {
                        paddedD = 8;
                    } else {
                        paddedD = Math.ceil(totalD + 4);
                    }
                    
                    paddedSize = `${paddedW}x${paddedD}x${paddedH}`;
                    paddedDims = parseBoxSize(paddedSize, false, true);
                    
                    // Use picture boxes for unframed pictures
                    isPictureItem = true; 
                } else {
                    const isFragileSubBox = (field.type || 'Fragile') === 'Fragile';
                    const isStandardSubBox = (field.type || '') === 'Standard';
                    paddedSize = calculatePaddedSize(item.dimensions, isFragileSubBox, false, isStandardSubBox);
                    paddedDims = parseBoxSize(paddedSize, isFragileSubBox);
                    
                    isPictureItem = itemNameLower.includes("man ray") || itemNameLower.includes("picture") || (!isUnframedPicture && itemNameLower.includes("frame")) || itemNameLower.includes("works") || itemNameLower.includes("painting") || categoryLower.includes("picture") || (!isUnframedPicture && (categoryLower.includes("frame") || notesLower.includes("framed"))) || isUnframedPicture || notesLower.includes("acrylic glass");
                }

                const pictureBoxes = ["24x6x18", "24x18x6", "30x24x6", "30x6x24", "36x8x30", "36x6x42", "40x8x50", "54x8x28", "48x6x32", "42x8x60"];
                const availableBoxes = isPictureItem 
                    ? BoxSize.value.filter(b => pictureBoxes.includes(b.value))
                    : BoxSize.value;

                const needTelescoping = needsTelescoping(paddedDims, availableBoxes);

                let boxSize = '';
                let isTelescoped = false;
                let telescopedBoxes = [];
                let telescopedCombined = '';

                if (needTelescoping) {
                    const telescoped = findTelescopedBox(paddedDims, availableBoxes);
                    if (telescoped && telescoped.originalBox) {
                        boxSize = telescoped.originalBox;
                        isTelescoped = true;
                        telescopedCombined = telescoped.value;
                        telescopedBoxes = Array(telescoped.numBoxes || 2).fill(telescoped.originalBoxTitle || telescoped.originalBox);
                    } else {
                        boxSize = '';
                    }
                } else {
                    boxSize = findClosestBoxSize(paddedSize, isPictureItem, true, !isUnframedPicture); 
                }

                subBoxesArray.push({
                    type: 'Fragile',
                    size: boxSize,
                    originalSize: item.dimensions,
                    paddedSize: paddedSize,
                    isUnframed: isUnframedPicture,
                    weight: item.weight || 0,
                    itemName: item.item_name || `Item ${idx + 1}`,
                    is_telescoped: isTelescoped,
                    telescoped_combined: telescopedCombined,
                    telescoped_boxes: telescopedBoxes,
                    selected_sizes: telescopedBoxes,
                    needs_telescoping: needTelescoping && !boxSize,
                    isPicture: isPictureItem
                });
            }
        });

        if (subBoxesArray.length > 1) {
            const combinedDimensions = calculateMainBoxSize(subBoxesArray);
            const finalConfirmSize = combinedDimensions?.formatted || '';
            const finalBoxSize = findClosestBoxSize(finalConfirmSize) || '';
            
            const totalWeight = subBoxesArray.reduce((sum, box) => sum + (parseFloat(box.weight) || 0), 0);
            
            field.subBoxes = [{
                type: 'Fragile',
                size: finalBoxSize,
                weight: totalWeight > 0 ? totalWeight.toString() : '20',
                itemName: 'Combined Items Box',
                is_telescoped: false,
                telescoped_boxes: [],
                selected_sizes: finalBoxSize ? [finalBoxSize] : []
            }];
        } else {
            field.subBoxes = subBoxesArray;
        }
    } else {
        field.subBoxes = [{
            type: field.type || 'Custom',
            size: '',
            weight: '20',
            itemName: 'Custom Sub-Box',
            is_telescoped: false,
            telescoped_boxes: [],
            selected_sizes: []
        }];
    }

    field.subBox = null;
    field.is_telescoped = false;
    
    updateMainBoxFromSubBoxes(field);
};


// Updated remove function to handle subBoxes array and restore original bounds
const removeSubBox_test = (index, subIndex) => {
    const field = fields_test.value[index];
    if (field.subBoxes && field.subBoxes.length > 0) {
        field.subBoxes.splice(subIndex, 1);
        if (field.subBoxes.length === 0) {
            field.subBoxes = null;
            field.subBox = null;
            
            // As explicitly requested: when subboxes are cleared, dynamically calculate confirm test size from the original item sizing with 2 inches padding per side
            const items = qoutationItems.value;
            if (items && items.length > 0) {
                const itemPaddedDims = [];
                items.forEach(item => {
                    if (item.dimensions) {
                        const itemNameLower = (item.item_name || item.name || "").toLowerCase();
                        const notesLower = (item.notes || item.item_notes || item.description || item.ItemNotes || "").toLowerCase();
                        const categoryLower = (item.category_name || item.category || item.item_category || item.type || item.item_type || "").toLowerCase();
                        const isUnframedPicture = itemNameLower.includes("unframe") || notesLower.includes("unframe") || categoryLower.includes("unframe");
                        
                        if (isUnframedPicture && item.is_duplicate_display) {
                            return;
                        }

                        let pSize = '';
                        let parsed = null;

                        if (isUnframedPicture) {
                            const qty = parseInt(item.quantity) || 1;
                            const value = item.dimensions.toLowerCase().replace(/×/g, 'x').replace(/\s+/g, ' ').trim();
                            const wMatch = value.match(/(\d+(?:\.\d+)?)\s*w/i);
                            const hMatch = value.match(/(\d+(?:\.\d+)?)\s*h/i);
                            const dMatch = value.match(/(\d+(?:\.\d+)?)\s*d/i);
                            const lMatch = value.match(/(\d+(?:\.\d+)?)\s*l/i);
                            let width = wMatch ? parseFloat(wMatch[1]) : null;
                            let height = hMatch ? parseFloat(hMatch[1]) : null;
                            let parsedDepth = dMatch ? parseFloat(dMatch[1]) : null;
                            if (lMatch && width === null) width = parseFloat(lMatch[1]);
                            
                            const numbers = value.match(/\d+(?:\.\d+)?/g);
                            if (width === null && height === null && numbers) {
                                if (numbers.length === 2) {
                                    width = parseFloat(numbers[0]);
                                    height = parseFloat(numbers[1]);
                                } else if (numbers.length >= 3) {
                                    width = parseFloat(numbers[0]);
                                    parsedDepth = parseFloat(numbers[1]);
                                    height = parseFloat(numbers[2]);
                                } else if (numbers.length === 1) {
                                    width = parseFloat(numbers[0]);
                                    height = parseFloat(numbers[0]);
                                }
                            }
                            if (width === null) width = 3;
                            if (height === null) height = 3;
                            
                            // Option A: Confirm test Box Size logic
                            const rawDepth = parsedDepth !== null ? parsedDepth : 6;
                            const totalD = qty > 1 ? (rawDepth + (qty - 1) * 0.5) : rawDepth;
                            
                            const paddedW = Math.ceil(width + 4);
                            const paddedH = Math.ceil(height + 4);
                            let paddedD = 6;
                            if (totalD <= 6) {
                                paddedD = 6;
                            } else if (totalD <= 8) {
                                paddedD = 8;
                            } else {
                                paddedD = Math.ceil(totalD + 4);
                            }
                            
                            pSize = `${paddedW}x${paddedD}x${paddedH}`;
                            parsed = parseBoxSize(pSize, false, true);
                            if (parsed) parsed.isUnframed = true;
                        } else {
                            const isFragileSubBox = (field.type || 'Fragile') === 'Fragile';
                            pSize = calculatePaddedSize(item.dimensions, isFragileSubBox);
                            parsed = parseBoxSize(pSize, isFragileSubBox);
                        }
                        if (parsed) itemPaddedDims.push(parsed);
                    }
                });
                
                if (itemPaddedDims.length > 0) {
                    const allAreUnframed = itemPaddedDims.every(d => d.isUnframed);
                    if (itemPaddedDims.length === 1) {
                        const d = itemPaddedDims[0];
                        field.confirm_size = `${d.l}x${d.h}x${d.w}`;
                    } else if (allAreUnframed) {
                        const maxL = Math.max(...itemPaddedDims.map(d => d.l));
                        const maxW = Math.max(...itemPaddedDims.map(d => d.w));
                        const finalD = Math.max(6, Math.max(...itemPaddedDims.map(d => d.h)));
                        field.confirm_size = `${maxL}x${finalD}x${maxW}`;
                    } else {
                        const mapped = itemPaddedDims.map(d => ({ paddedSize: `${d.l}x${d.h}x${d.w}` }));
                        const res = calculateMainBoxSize(mapped);
                        if (res) field.confirm_size = res.formatted;
                    }
                }
            } else if (field.original_confirm_size) {
                field.confirm_size = field.original_confirm_size;
            }
            
            if (field.original_type) {
                field.type = field.original_type;
            }
            
            autoSelectTelescopeBoxes(field);
        } else {
            updateMainBoxFromSubBoxes(field);
        }
    } else {
        field.subBox = null;
        field.is_telescoped = false;
    }
};
// const removeSubBox_test = (index) => {
//     fields_test.value[index].subBox = null;
// };

const updateMainBoxFromSubBoxes = (field_test) => {
    if (!field_test.subBoxes || field_test.subBoxes.length === 0) return;

    // Map sub-boxes based on whether they were manually selected via dropdown or preloaded normally
    const paddedSubBoxes = field_test.subBoxes.map(sb => {
        // As explicitly requested: if the subbox has a telescope, confirm size evaluates directly from the item size plus exactly 6 inches total (increasing 2 inches more)
        if (sb.is_telescoped && sb.originalSize) {
            const paddedBaseStr = calculatePaddedSize(sb.originalSize);
            if (paddedBaseStr) {
                const baseDims = parseBoxSize(paddedBaseStr);
                if (baseDims) {
                    return { paddedSize: `${baseDims.l + 4}x${baseDims.h + 4}x${baseDims.w + 4}` };
                }
            }
        }
        
        // Dynamically extract selected sub-box item bounds and pad with exactly 4 inches to continuously update outer Box test Size and Confirm Size
        let targetStr = sb.is_telescoped ? (sb.telescoped_combined || sb.size) : sb.size;
        
        if (!targetStr && Array.isArray(sb.selected_sizes) && sb.selected_sizes.length > 0) {
            targetStr = sb.selected_sizes[0];
        }
        
        if (targetStr) {
            const dims = parseBoxSize(targetStr);
            if (dims) {
                // always add exactly 4 inches to the total inner sub-box sizing across all dimensions
                return { paddedSize: `${dims.l + 4}x${dims.h + 4}x${dims.w + 4}` };
            }
        }
        if (sb.paddedSize) {
            return { paddedSize: sb.paddedSize };
        }
        return null;
    }).filter(sb => sb && sb.paddedSize);

    if (paddedSubBoxes.length > 0) {
        // Calculate the bounding box for these padded sub-boxes
        const result = calculateMainBoxSize(paddedSubBoxes);
        
        if (result) {
            // Keep the exact calculated padded size as the Confirm Box Size
            field_test.confirm_size = result.formatted;
            
            // Automatically set main box type to Fragile
            field_test.type = 'Fragile';
            // Apply single match or multiselect telescoping automatically
            autoSelectTelescopeBoxes(field_test);
        }
    }
};
const removeField_test = (index) => {
    if (fields_test.value.length > 1) fields_test.value.splice(index, 1);
};
const autoSelectTelescopeBoxes = (field_test) => {
    if (!field_test.confirm_size) return;
    
    const sizeStr = field_test.confirm_size.toLowerCase().replace(/[^0-9x]/g, '');
    const isFragileMain = (field_test.type || 'Fragile') === 'Fragile';
    const targetDims = parseBoxSize(sizeStr, isFragileMain);
    if (!targetDims) return;
    const hasUnframed = field_test.subBoxes && field_test.subBoxes.some(sb => sb.isUnframed);
    
    // First check if any single predefined box is equal or larger to fit the confirm size perfectly
    const singleMatch = findClosestBoxSize(sizeStr, hasUnframed, true, !hasUnframed); // Skip waste limit and enforce picture boxes if unframed
    const matchingBoxItem = BoxSize.value.find(b => b.value && b.value.toLowerCase() === singleMatch.toLowerCase());
    if (singleMatch && matchingBoxItem) {
        field_test.selected_sizes = [matchingBoxItem.title];
        field_test.size = matchingBoxItem.value;
        field_test.is_telescoped = false;
        field_test.telescoped_combined = '';
        field_test.primary_box = matchingBoxItem.title;
        field_test.secondary_box = '';
        return;
    }
    
    // If it's an unframed picture, DO NOT use telescoping, even if it falls through (shouldn't happen since waste limit is ignored above)
    if (hasUnframed) return;

    // Fallback to telescoping if single match not found
    
    let bestMatch = null;
    let bestScore = Infinity;
    const OVERLAP = 1; // Changed from 4 to 1
    
    const validBoxes = BoxSize.value.filter(box => box.value && box.value !== '');
    
    for (let i = 0; i < validBoxes.length; i++) {
        const boxDims = parseBoxSize(validBoxes[i].value);
        if (!boxDims) continue;
        
        for (let numBoxes = 2; numBoxes <= 3; numBoxes++) {
            const combinedL = boxDims.l;
            const combinedH = boxDims.h;
            const combinedW = (boxDims.w * numBoxes) - ((numBoxes - 1) * OVERLAP);

            const diffL = combinedL - targetDims.l;
            const diffH = combinedH - targetDims.h;
            const diffW = combinedW - targetDims.w;

            // Heavy penalties if the box is smaller than required (to force finding a box that fits if possible)
            const penaltyL = diffL < 0 ? Math.abs(diffL) * 100000000 : 0;
            const penaltyH = diffH < 0 ? Math.abs(diffH) * 10000000 : 0;
            const penaltyW = diffW < 0 ? Math.abs(diffW) * 1000000 : 0;

            // Tie-breakers for boxes that DO fit: minimize excess size (preferring width/depth matches)
            const excessL = diffL > 0 ? diffL * 100000 : 0;
            const excessH = diffH > 0 ? diffH * 1000 : 0;
            const excessW = diffW > 0 ? diffW : 0;

            const score = penaltyL + penaltyH + penaltyW + excessL + excessH + excessW;

            if (score < bestScore) {
                    bestScore = score;
                    bestMatch = {
                        box1Title: validBoxes[i].title,
                        numBoxes: numBoxes,
                        combinedSize: `${combinedL}x${combinedH}x${combinedW}`
                    };
                }
        }
    }
    
    if (bestMatch) {
        field_test.selected_sizes = Array(bestMatch.numBoxes).fill(bestMatch.box1Title);
        field_test.is_telescoped = true;
        field_test.type = 'Fragile';
        field_test.telescoped_combined = bestMatch.combinedSize;
        field_test.primary_box = bestMatch.box1Title;
        field_test.secondary_box = bestMatch.numBoxes >= 2 ? bestMatch.box1Title : '';
        const matchedBoxObj = BoxSize.value.find(b => b.title === bestMatch.box1Title);
        field_test.size = matchedBoxObj ? matchedBoxObj.value : bestMatch.box1Title;
    } else {
        field_test.selected_sizes = [];
        field_test.size = '';
        field_test.is_telescoped = false;
        field_test.telescoped_combined = '';
        field_test.primary_box = '';
        field_test.secondary_box = '';
    }
};

const onConfirmSizeInput_test = (field_test) => {
    field_test.confirm_size = field_test.confirm_size.toLowerCase().replace(/[^0-9x]/g, '');
};

const toggleBoxSelection = (item, identifier, isRemove = false) => {
    if (!Array.isArray(item.selected_sizes)) {
        item.selected_sizes = [];
    }

    if (isRemove) {
        const index = item.selected_sizes.indexOf(identifier);
        if (index > -1) {
            item.selected_sizes.splice(index, 1);
        }
    } else {
        if (item.selected_sizes.length > 0 && item.selected_sizes[0] !== identifier) {
            item.selected_sizes = [identifier];
        } else if (item.selected_sizes.length === 0) {
            item.selected_sizes = [identifier];
        } else if (item.selected_sizes.length === 1) {
            item.selected_sizes.push(identifier);
        } else if (item.selected_sizes.length === 2) {
            item.selected_sizes.push(identifier);
        } else if (item.selected_sizes.length >= 3) {
            item.selected_sizes = [];
        }
    }
    recalculateCombinedSize(item);
};

const recalculateCombinedSize = (item) => {
    // When 2 or 3 boxes are selected, calculate combined telescoped size
    if (item.selected_sizes.length >= 2) {
        const boxData = BoxSize.value.find(b => b.title === item.selected_sizes[0]);
        
        if (boxData) {
            const boxDims = parseBoxSize(boxData.value);
            
            if (boxDims) {
                const OVERLAP = 1;
                const numBoxes = item.selected_sizes.length;
                // For identical boxes, orientation is same
                const combinedLength = boxDims.l;
                const combinedHeight = boxDims.h;
                const combinedWidth = (boxDims.w * numBoxes) - ((numBoxes - 1) * OVERLAP);
                
                item.telescoped_combined = `${combinedLength}x${combinedHeight}x${combinedWidth}`;
                item.is_telescoped = true;
                item.primary_box = item.selected_sizes[0];
                item.secondary_box = item.selected_sizes[1];
            }
        }
    } else {
        item.is_telescoped = false;
        item.telescoped_combined = '';
        item.primary_box = '';
        item.secondary_box = '';
    }
    item.size = item.selected_sizes[0] || '';
};

const incrementBox = (item) => {
    if (!item.selected_sizes || item.selected_sizes.length === 0) return;
    if (item.selected_sizes.length < 3) {
        item.selected_sizes.push(item.selected_sizes[0]);
        recalculateCombinedSize(item);
    }
};

const decrementBox = (item) => {
    if (!item.selected_sizes || item.selected_sizes.length <= 1) return;
    item.selected_sizes.pop();
    recalculateCombinedSize(item);
};

const clearBoxSelection = (item) => {
    item.selected_sizes = [];
    recalculateCombinedSize(item);
};


const addField = () => {
    fields.value.push({ type: '', size: '', confirm_size: '', weight: '20', subBox: null });
};
// const addSmaller = ref(false);
const addSubBox = (index) => {
    fields.value[index].subBox = {
        type: '',
        size: '',
        weight: '20'
    };
};

const updateStandardMainBox = (field) => {
    if (!field.subBox || !field.subBox.size) return;
    const dims = parseBoxSize(field.subBox.size);
    if (!dims) return;
    
    // Always add exactly 2 inches to each side (+4 inches total per dimension)
    const paddedStr = `${dims.l + 4}x${dims.h + 4}x${dims.w + 4}`;
    field.confirm_size = paddedStr;
    
    const singleMatch = findClosestBoxSize(paddedStr);
    const matchingBoxItem = BoxSize.value.find(b => b.value && b.value.toLowerCase() === singleMatch.toLowerCase());
    if (singleMatch && matchingBoxItem) {
        field.selected_sizes = [matchingBoxItem.title];
        field.size = matchingBoxItem.value;
        field.is_telescoped = false;
    } else {
        field.size = paddedStr;
        field.selected_sizes = [paddedStr];
        field.is_telescoped = false;
    }
};

const removeSubBox = (index) => {
    const field = fields.value[index];
    field.subBox = null;
    if (field.original_confirm_size) {
        field.confirm_size = field.original_confirm_size;
        const singleMatch = findClosestBoxSize(field.confirm_size);
        const matchingBoxItem = BoxSize.value.find(b => b.value && b.value.toLowerCase() === singleMatch.toLowerCase());
        if (singleMatch && matchingBoxItem) {
            field.selected_sizes = [matchingBoxItem.title];
            field.size = matchingBoxItem.value;
        } else {
            field.size = field.confirm_size;
            field.selected_sizes = [field.confirm_size];
        }
    }
};

const isTestModeActive = computed(() => {
    return true; // ALWAYS use test mode on the Test Quotation page
});


const BoxSize = ref([
    { title: "Select Box Size", value: "" },
    { title: "9x6x3", value: "9x6x3" },
    { title: "12x10x3", value: "12x10x3" },
    { title: "12x12x6", value: "12x12x6" },
    { title: "15x12x10", value: "15x12x10" },
    { title: "16x12x8", value: "16x12x8" },
    { title: "16x16x4", value: "16x16x4" },
    { title: "17x11x8", value: "17x11x8" },
    { title: "17x17x8", value: "17x17x8" },
    { title: "18x14x12", value: "18x14x12" },
    { title: "18x16x14", value: "18x16x14" },
    { title: "20x20x12", value: "20x20x12" },
    { title: "20x16x16", value: "20x16x16" },
    { title: "20x14x6", value: "20x14x6" },
    { title: "20x12x12", value: "20x12x12" },
    { title: "22x18x16", value: "22x18x16" },
    { title: "24x16x12", value: "24x16x12" },
    { title: "24x18x18", value: "24x18x18" },
    { title: "24x24x16", value: "24x24x16" },
    { title: "26x20x8", value: "26x20x8" },
    { title: "26x20x20", value: "26x20x20" },
    { title: "29x24x24", value: "29x24x24" },
    { title: "30x30x16", value: "30x30x16" },
    { title: "28x16x7", value: "28x16x7" },
    { title: "36x20x9", value: "36x20x9" },
    { title: "24x6x6", value: "24x6x6" },
    { title: "36x8x8", value: "36x8x8" },
    { title: "6x6x6", value: "6x6x6" },
    { title: "8x8x8", value: "8x8x8" },
    { title: "10x10x10", value: "10x10x10" },
    { title: "12x12x12", value: "12x12x12" },
    { title: "14x14x14", value: "14x14x14" },
    { title: "16x16x16", value: "16x16x16" },
    { title: "18x18x18", value: "18x18x18" },
    { title: "20x20x20", value: "20x20x20" },
    { title: "22x22x22", value: "22x22x22" },
    { title: "24x24x24", value: "24x24x24" },
    { title: "24x24x24 DW", value: "24x24x24" },
    { title: "26x26x26", value: "26x26x26" },
    { title: "28x28x28", value: "28x28x28" },
    { title: "32x32x32 DW 275", value: "32x32x32" },
    { title: "34x34x34", value: "34x34x34" },
    { title: "36x36x36", value: "36x36x36" },
    { title: "24x6x18 275 lb", value: "24x6x18" },
    { title: "24x18x6", value: "24x18x6" },
    { title: "30x24x6", value: "30x24x6" },
    { title: "30x6x24 275 lb", value: "30x6x24" },
    { title: "36x8x30 275 lb", value: "36x8x30" },
    { title: "36x6x42 275 lb", value: "36x6x42" },
    { title: "40x8x50 275 lb", value: "40x8x50" },
    { title: "54x8x28 275 lb (bicycle)", value: "54x8x28" },
    { title: "48x6x32-72 275 lb (Art Jumbo)", value: "48x6x32" },
    { title: "42x8x60 400 lb Dw", value: "42x8x60" },
    { title: "6x6x48 (Pole)", value: "6x6x48" },

    { title: "10x10x48 (Golf /Umbrella)", value: "10x10x48" },
    { title: "15x15x48 (Golf Bag)", value: "15x15x48" },
    { title: "18x18x28 (Dish Pack)", value: "18x18x28" },
    { title: "20x8x50 (Guitar)", value: "20x8x50" },
    { title: "24x13x31 (suitecase)", value: "24x13x31" },
    { title: "35x22x22 (EH)", value: "35x22x22" },
    { title: "40x18x8 (Laydown)", value: "40x18x8" },
    { title: "54x8x28 275 lb (bicycle)", value: "54x8x28" },

    { title: "9x6x3 (Medium Secruity)", value: "9x6x3" },
    { title: "12x10x3 (Large Security)", value: "12x10x3" },
    { title: "12x12x6 UPS-13", value: "12x12x6" },
    { title: "15x12x10 UPS-21", value: "15x12x10" },
    { title: "16x12x8", value: "16x12x8" },
    { title: "16x16x4 UPS-17", value: "16x16x4" },
    { title: "16x16x8 UPS-31", value: "16x16x8" },
    { title: "17x11x8 UPS-27", value: "17x11x8" },
    { title: "17x17x8 UPS -29", value: "17x17x8" },
    { title: "18x14x12 (1680670 #44)", value: "18x14x12" },
    { title: "20x20x12 UPS-60", value: "20x20x12" },
    { title: "20x12x12 UPS-34", value: "20x12x12" },
    { title: "24x18x18 UPS-46B", value: "24x18x18" },
    { title: "24x24x16 UPS-47B", value: "24x24x16" },
    { title: "17x17x8 Laptop (All-in-one) ", value: "17x17x8" },
    { title: "10x5x3 Cell Phone (All-in-one)", value: "10x5x3" },

]);
// console.log("Available Box Sizes:",BoxSize.value);

// parseSize moved inside getSingleQuote where used
// ========== BOX PARSER (NO PADDING) ==========
const parseBoxSize = (size, isFragile = false) => {
    if (!size || typeof size !== 'string') return null;

    const value = size
        .toLowerCase()
        .replace(/×/g, 'x')
        .replace(/\s+/g, ' ')
        .trim();

    const numbers = value.match(/\d+(?:\.\d+)?/g);
    if (!numbers) return null;

    let l, w, h;

    const lMatch = value.match(/(\d+(?:\.\d+)?)\s*l/i);
    const wMatch = value.match(/(\d+(?:\.\d+)?)\s*w/i);
    const hMatch = value.match(/(\d+(?:\.\d+)?)\s*h/i);
    const dMatch = value.match(/(\d+(?:\.\d+)?)\s*d/i);

    if (lMatch) l = parseFloat(lMatch[1]);
    if (wMatch) w = parseFloat(wMatch[1]);
    if (hMatch) h = parseFloat(hMatch[1]);
    if (dMatch && l === undefined) l = parseFloat(dMatch[1]);

    if (l === undefined && w === undefined && h === undefined) {
        if (numbers.length === 2) {
            l = parseFloat(numbers[0]); 
            h = isFragile ? 3 : 2;                      
            w = parseFloat(numbers[1]); 
        } else if (numbers.length >= 3) {
            l = parseFloat(numbers[0]);
            h = parseFloat(numbers[1]); // Depth centrally mapped as requested
            w = parseFloat(numbers[2]);
        } else if (numbers.length === 1) {
            l = parseFloat(numbers[0]);
            h = parseFloat(numbers[0]);
            w = parseFloat(numbers[0]);
            
        } else {
            return null;
        }
    }

    // Default any remaining individual missing dimensions safely if some matched
    if (l === undefined) l = 3;
    if (w === undefined) w = l;
    if (h === undefined) h = w;

    return { l, w, h };
};


const removeField = (index) => {
    if (fields.value.length > 1) fields.value.splice(index, 1);
};

// const applyautomaticBox = ref(false);

// Commented out unused applyAutomaticBox to resolve ESLint compile error
// const applyAutomaticBox = () => {
//     fields_test.value = fields_test.value.map(field => {
//         if (field.type === "Fragile") {
//             return field;
//         }
//         if (field.type === "Custom") {
//             return field;
//         }
//         return {
//             ...field,
//             size: bestBox.value?.value || ''
//         };
//     });
// };
const showPrizeCard = ref(false);

const prizes = ref([
    { label: ' Next Day Air:', value: '0', code: '', insurance: '', signature: '', baseServiceCharge: 0.00, allCharges: [] },
    { label: ' Second Day:', value: '0', code: '', insurance: '', signature: '', baseServiceCharge: 0.00, allCharges: [] },
    { label: ' Third Day:', value: '0', code: '', insurance: '', signature: '', baseServiceCharge: 0.00, allCharges: [] },
    { label: ' Ground Shipping:', value: '0', code: '', insurance: '', signature: '', baseServiceCharge: 0.00, allCharges: [] }
]);

const isPrice = ref(false);
const isMail = ref(false);
const qoutationItems = ref([]);
const qoutationData = ref('');
const boxCost = ref(0);
const packagingCost = ref(0);
const pickupCharge = ref(0);
const shippingCost = ref(0);
const signatureCost = ref(0);
const insuranceCost = ref(0);
const selectedShippingCode = ref('');
const boxesCount = ref([]);
const signature_per_box = ref(0);
const signature_domestic = ref(0);
const signature_international = ref(0);
const insurance_price = ref(0);

const masterBoxesCount = computed(() => {
    const currentFields = isGetQuote.value ? fields.value : fields_test.value;
    if (!currentFields || currentFields.length === 0) {
        return boxesCount.value?.length || 1;
    }
    let count = 0;
    currentFields.forEach(field => {
        if (field.is_telescoped && Array.isArray(field.selected_sizes) && field.selected_sizes.length >= 2) {
            count += field.selected_sizes.length;
        } else if (Array.isArray(field.selected_sizes) && field.selected_sizes.length > 1) {
            count += field.selected_sizes.length;
        } else {
            count += 1;
        }
    });
    return count > 0 ? count : (boxesCount.value?.length || 1);
});
// const allCharges = ref([]);
const cms_processing_fee = ref(0);
const insurance_domestic = ref(0);
const insurance_international = ref(0);
const amount_exceeds_limit = ref(false);
const max_tier_value = ref(0);
const totalCost = computed(() => {
    return parseFloat(boxCost.value) + parseFloat(packagingCost.value) + parseFloat(pickupCharge.value == '' ? 0 : pickupCharge.value) + parseFloat(shippingCost.value) + parseFloat(signatureCost.value) + parseFloat(insuranceCost.value) + parseFloat(cms_processing_fee.value);
});


// function parseSize(str) {
//   const [l, w, h] = str.split('x').map(Number);
//   return { l, w, h, volume: l * w * h };
// }

// function isSmaller(child, parent) {
//   if (!child || !parent) return false;

//   const c = parseSize(child);
//   const p = parseSize(parent);

//   return c.l <= p.l && c.w <= p.w && c.h <= p.h && c.volume < p.volume;
// }

// const getValidSubBoxOptions = (parentSize) => {
//   return BoxSize.value.filter(box => {
//     return box.value !== parentSize && isSmaller(box.value, parentSize);
//   });
// };



const isConfirmSizeValid = (str) => {
    const pattern = /^(\d+)x(\d+)x(\d+)$/;
    return pattern.test(str.trim());
};

// Called on input to filter allowed characters (optional, but useful)
const onConfirmSizeInput = (field) => {
    // Filter input to allow only digits and 'x'
    field.confirm_size = field.confirm_size.toLowerCase().replace(/[^0-9x]/g, '');
};

// Clean and standardize confirm_size input
const normalizeSizeInput = (size) => {
    return size
        .toLowerCase()
        .replace(/[^0-9x]/g, '')     // keep only digits and 'x'
        .replace(/x{2,}/g, 'x')      // collapse multiple x to one
        .trim();                     // trim spaces only
};


const getPureBoxValue = (sizeIdentifier) => {
    if (!sizeIdentifier) return '';
    const found = BoxSize.value.find(b => b.title === sizeIdentifier || b.value === sizeIdentifier);
    if (found && found.value) return found.value;
    const cleaned = sizeIdentifier.toLowerCase().split(' ')[0];
    return cleaned;
};

const submit = () => {
    if (amount_exceeds_limit.value) {
        store.commit('setErrorNotification', `Amount exceeds maximum allowed insurance tier limit of $${Number(max_tier_value.value).toFixed(2)}`);
        setTimeout(() => {
            store.commit('clearNotifications');
        }, 7000);
        return;
    }
    errors.value = [];
    fields.value.forEach((field, index) => {

        if (field.confirm_size) {
            field.confirm_size = normalizeSizeInput(field.confirm_size);
        }

        const hasValidConfirmSize = field.confirm_size && isConfirmSizeValid(field.confirm_size);

        if (!field.type || !field.size || !field.weight) {
            errors.value.push(`Box ${index + 1}: Type and Size and Weight must be selected.`);
            store.commit('setErrorNotification', `Box ${index + 1}: Type and Size and Weight must be selected.`);
        }
        if (field.confirm_size && !hasValidConfirmSize) {
            errors.value.push(`Box ${index + 1}: Confirm Box Size format is invalid.`);
            store.commit('setErrorNotification', `Box ${index + 1}: Confirm Box Size format is invalid.`);
        }
        if (field?.subBox != null && (field?.subBox.type === '' || field?.subBox.size === '')) {
            errors.value.push(`Sub Box ${index + 1}: Sub Box Type and Size  must be selected.`);
            store.commit('setErrorNotification', `Sub Box ${index + 1}: Sub Box Type and Size must be selected.`);
        }
        // if(field?.subBox != null){
        //     errors.value.push(`Sub Box ${index + 1}: Sub Box Size must be selected.`);
        //     store.commit('setErrorNotification', `Sub Box ${index + 1}: Sub Box Size must be selected.`);
        // }
        // if(field.subBox && field.subBox.weight && isNaN(field.subBox.weight)){
        //     errors.value.push(`Sub Box ${index + 1}: Sub Box Weight must be a number.`);
        //     store.commit('setErrorNotification', `Sub Box ${index + 1}: Sub Box Weight must be a number.`);
        // }
        if (isNaN(field.weight)) {
            errors.value.push(`Box ${index + 1}: Weight must be a number.`);
            store.commit('setErrorNotification', `Box ${index + 1}: Weight must be a number.`);
        }
        if (field.weight && field.weight <= 0) {
            errors.value.push(`Box ${index + 1}: Weight must be greater than zero.`);
            store.commit('setErrorNotification', `Box ${index + 1}: Weight must be greater than zero.`);
        }
        // if (field.subBox && field.subBox.weight && field.subBox.weight <= 0) {
        //     errors.value.push(`Sub Box ${index + 1}: Sub Box Weight must be greater than zero.`);
        //     store.commit('setErrorNotification', `Sub Box ${index + 1}: Sub Box Weight must be greater than zero.`);
        // }
    });
    setTimeout(() => {
        store.commit('clearNotifications');
    }, 7000);
    if (errors.value.length > 0) {
        return;
    }
    isPrice.value = true;
    
    const payloadBoxes = [];
    fields.value.forEach(field => {
        if (field.is_telescoped && Array.isArray(field.selected_sizes) && field.selected_sizes.length >= 2) {
            field.selected_sizes.forEach(sizeTitle => {
                payloadBoxes.push({
                    ...field,
                    size: getPureBoxValue(sizeTitle)
                });
            });
        } else {
            payloadBoxes.push({
                ...field,
                size: field.size
            });
        }
    });

    // axios.post("api/addBoxesTest", { id: decodeURIComponent(atob(route.params.id)), boxes: payloadBoxes }).then((response) => {
        axios.post("api/addBoxesTest", { id: decodeURIComponent(atob(route.params.id)), boxes: payloadBoxes, pickupCharge: pickupCharge.value === '' ? 0 : pickupCharge.value }).then((response) => {
        console.log('check:', response.data);
        const extractedBoxCost = response.data?.total_box_price ?? response.data?.quote?.total_box_price ?? response.data?.quote?.box_cost ?? response.data?.quote?.boxCost;
        const extractedPkgCost = response.data?.total_packaging_cost ?? response.data?.quote?.total_packaging_cost ?? response.data?.quote?.packaging_cost ?? response.data?.quote?.packagingCost;
        if (extractedBoxCost !== undefined && extractedBoxCost !== null) boxCost.value = extractedBoxCost;
        if (extractedPkgCost !== undefined && extractedPkgCost !== null) packagingCost.value = extractedPkgCost;
        getSingleQuote();
        shippingCost.value = 0;
        hasClickedTestPrice.value = true;
        showPrizeCard.value = true;
        store.commit('setSuccessNotification', "Price fetched successfully");
        store.commit('setErrorNotification', null);
        setTimeout(() => {
            store.commit('clearNotifications');
        }, 7000);
        isPrice.value = false;
        // showModal.value = false;
    }).catch((error) => {
        isPrice.value = false;
        showPrizeCard.value = false;
        if (error.response?.data && error.response?.data.error) {
            store.commit('setErrorNotification', error.response.data.error);
            store.commit('setSuccessNotification', null);
        }
        setTimeout(() => {
            store.commit('clearNotifications');
        }, 7000);
        console.error(error);
    });
};
const submit_test = () => {
    if (amount_exceeds_limit.value) {
        store.commit('setErrorNotification', `Amount exceeds maximum allowed insurance tier limit of $${Number(max_tier_value.value).toFixed(2)}`);
        setTimeout(() => {
            store.commit('clearNotifications');
        }, 7000);
        return;
    }
    errors.value = [];
    fields_test.value.forEach((field, index) => {

        if (field.confirm_size) {
            field.confirm_size = normalizeSizeInput(field.confirm_size);
        }

        const hasValidConfirmSize = field.confirm_size && isConfirmSizeValid(field.confirm_size);
        
        const hasValidSize = field.is_telescoped 
            ? (field.selected_sizes && field.selected_sizes.length >= 2)
            : !!field.size;

        if (!field.type || !hasValidSize || !field.weight) {
            errors.value.push(`Box ${index + 1}: Type and Size and Weight must be selected.`);
            store.commit('setErrorNotification', `Box ${index + 1}: Type and Size and Weight must be selected.`);
        }
        if (field.confirm_size && !hasValidConfirmSize) {
            errors.value.push(`Box ${index + 1}: Confirm Box Size format is invalid.`);
            store.commit('setErrorNotification', `Box ${index + 1}: Confirm Box Size format is invalid.`);
        }
        if (field?.subBox != null && (field?.subBox.type === '' || field?.subBox.size === '')) {
            errors.value.push(`Sub Box ${index + 1}: Sub Box Type and Size  must be selected.`);
            store.commit('setErrorNotification', `Sub Box ${index + 1}: Sub Box Type and Size must be selected.`);
        }
        // if(field?.subBox != null){
        //     errors.value.push(`Sub Box ${index + 1}: Sub Box Size must be selected.`);
        //     store.commit('setErrorNotification', `Sub Box ${index + 1}: Sub Box Size must be selected.`);
        // }
        // if(field.subBox && field.subBox.weight && isNaN(field.subBox.weight)){
        //     errors.value.push(`Sub Box ${index + 1}: Sub Box Weight must be a number.`);
        //     store.commit('setErrorNotification', `Sub Box ${index + 1}: Sub Box Weight must be a number.`);
        // }
        if (isNaN(field.weight)) {
            errors.value.push(`Box ${index + 1}: Weight must be a number.`);
            store.commit('setErrorNotification', `Box ${index + 1}: Weight must be a number.`);
        }
        if (field.weight && field.weight <= 0) {
            errors.value.push(`Box ${index + 1}: Weight must be greater than zero.`);
            store.commit('setErrorNotification', `Box ${index + 1}: Weight must be greater than zero.`);
        }
        // if (field.subBox && field.subBox.weight && field.subBox.weight <= 0) {
        //     errors.value.push(`Sub Box ${index + 1}: Sub Box Weight must be greater than zero.`);
        //     store.commit('setErrorNotification', `Sub Box ${index + 1}: Sub Box Weight must be greater than zero.`);
        // }
    });
    setTimeout(() => {
        store.commit('clearNotifications');
    }, 7000);
    if (errors.value.length > 0) {
        return;
    }
    isPrice.value = true;
    hasClickedTestPrice.value = true;
    
    // Expand telescoped boxes so backend charges the cost for both physical boxes used
    const payloadBoxes = [];
    fields_test.value.forEach(field => {
        const mappedSubBox = (field.subBoxes && field.subBoxes.length > 0) ? {
            type: field.subBoxes[0].type || 'Fragile',
            size: field.subBoxes[0].size || '',
            weight: field.subBoxes[0].weight || '20'
        } : field.subBox;

        if (field.is_telescoped && Array.isArray(field.selected_sizes) && field.selected_sizes.length >= 2) {
            field.selected_sizes.forEach(sizeTitle => {
                payloadBoxes.push({
                    ...field,
                    subBox: mappedSubBox,
                    size: getPureBoxValue(sizeTitle)
                });
            });
        } else {
            payloadBoxes.push({
                ...field,
                subBox: mappedSubBox,
                size: getPureBoxValue(field.size)
            });
        }
    });

    axios.post("api/addBoxesTest", { id: decodeURIComponent(atob(route.params.id)), boxes: payloadBoxes }).then((response) => {
        console.log('check:', response.data);
        const extractedBoxCost = response.data?.total_box_price ?? response.data?.quote?.total_box_price ?? response.data?.quote?.box_cost ?? response.data?.quote?.boxCost;
        const extractedPkgCost = response.data?.total_packaging_cost ?? response.data?.quote?.total_packaging_cost ?? response.data?.quote?.packaging_cost ?? response.data?.quote?.packagingCost;
        if (extractedBoxCost !== undefined && extractedBoxCost !== null) boxCost.value = extractedBoxCost;
        if (extractedPkgCost !== undefined && extractedPkgCost !== null) packagingCost.value = extractedPkgCost;
        getSingleQuote();
        shippingCost.value = 0;
        showPrizeCard.value = true;
        store.commit('setSuccessNotification', "Price fetched successfully");
        store.commit('setErrorNotification', null);
        setTimeout(() => {
            store.commit('clearNotifications');
        }, 7000);
        isPrice.value = false;
        // showModal.value = false;
    }).catch((error) => {
        isPrice.value = false;
        showPrizeCard.value = false;
        if (error.response?.data && error.response?.data.error) {
            store.commit('setErrorNotification', error.response.data.error);
            store.commit('setSuccessNotification', null);
        }
        setTimeout(() => {
            store.commit('clearNotifications');
        }, 7000);
        console.error(error);
    });
};

// const getSingleQuote = () => {
//     axios.post("api/get-single-qoute-test", { id: decodeURIComponent(atob(route.params.id)) }).then((response) => {
//         console.log("All Data",response.data);
//         // console.log("Full Response:", response);
//         // console.log("Response Data:", response.data);
//         // console.log("Quote:", response.data?.quote);
//         // console.log("Boxes:", response.data?.boxes);
//         console.log("Items:", response.data?.quote?.test_items || response.data?.quote?.items);
//         const items = response.data?.quote?.test_items || response.data?.quote?.items || [];
//         console.log("Items Array:",items);
//         function getPaddedDimensions(size, padding = 2) {
//             const parsed = parseSize(size);

//             if (!parsed) return null;

//             const width = parsed.width || 0;
//             const height = parsed.height || 2; // default thickness

//             return {
//                 originalWidth: width,
//                 originalHeight: height,
//                 paddedWidth: width + (padding * 2),
//                 paddedHeight: height + (padding * 2)
//             };
//         }
//         function parseSize(size) {
//             if (!size) return null;

//             const value = size
//                 .toLowerCase()
//                 .replace(/×/g, 'x')       // fix × symbol
//                 .replace(/\s+/g, ' ')     // normalize spaces
//                 .trim();

//             // console.log("Normalized:", value);

//             const lMatch = value.match(/(\d+(\.\d+)?)\s*l/);
//             const wMatch = value.match(/(\d+(\.\d+)?)\s*w/);
//             const dMatch = value.match(/(\d+(\.\d+)?)\s*d/);
//             const hMatch = value.match(/(\d+(\.\d+)?)\s*h/);

//             // console.log("Matches:", { lMatch, wMatch, dMatch, hMatch });

//             const l = lMatch ? parseFloat(lMatch[1]) : null;
//             const w = wMatch ? parseFloat(wMatch[1]) : null;

//             // 👇 IMPORTANT: treat depth as height fallback
//             const h = hMatch
//                 ? parseFloat(hMatch[1])
//                 : (dMatch ? parseFloat(dMatch[1]) : null);

//             // ✅ fallback for "14x12x11"
//             const numbers = value.match(/\d+(\.\d+)?/g);

//             const final = {
//                 l: l ?? (numbers?.[0] ? parseFloat(numbers[0]) : null),
//                 w: w ?? (numbers?.[1] ? parseFloat(numbers[1]) : null),
//                 h: h ?? (numbers?.[2] ? parseFloat(numbers[2]) : null),
//             };

//             // console.log("Final parsed:", final);

//             // ❌ Only fail if EVERYTHING is missing
//             if (final.l === null && final.w === null && final.h === null) {
//                 return null;
//             }

//             return final;
//         }

//         const padding = 2;
//         let allItems = [];

//         // ✅ Collect items
//         items.forEach((item, index) => {
//             const size = item.size || item.dimensions;
//             console.log(`Item ${index + 1} size:`, size);
//             if (!size) {
//                 console.warn(`Item ${index + 1} missing size`, item);
//                 return;
//             }

//             const parsed = parseSize(size);

//             if (!parsed || parsed.l == null || parsed.w == null || parsed.h == null) {
//                 console.warn(`Item ${index + 1}: Invalid format`, size);
//                 return;
//             }

//             allItems.push(parsed);
//         });


//         if (!allItems.length) {
//             console.warn("No valid items found");
//             return;
//         }

//         let finalDims;


//         if (allItems.length === 1) {
//             const item = allItems[0];

//             finalDims = {
//                 l: item.l + (padding * 2),
//                 w: item.w + (padding * 2),
//                 h: item.h + (padding * 2),
//             };

//             console.log("Single Item Final:", finalDims);
//         }


//         else {
//             let totalL = 0;
//             let maxW = 0;
//             let maxH = 0;

//             allItems.forEach(item => {
//                 totalL += item.l;
//                 maxW = Math.max(maxW, item.w);
//                 maxH = Math.max(maxH, item.h);
//             });

//             finalDims = {
//                 l: totalL + (padding * 2),
//                 w: maxW + (padding * 2),
//                 h: maxH + (padding * 2),
//             };

//             console.log("Multiple Items Final:", finalDims);
//         }


//         function canFit(item, box) {
//             if (!item || !box) return false;

//             // const itemDims = [item.l, item.w, item.h];
//             const boxDims = [box.l, box.w, box.h];

//             // All 6 rotations of the box
//             const permutations = [
//                 [boxDims[0], boxDims[1], boxDims[2]],
//                 [boxDims[0], boxDims[2], boxDims[1]],
//                 [boxDims[1], boxDims[0], boxDims[2]],
//                 [boxDims[1], boxDims[2], boxDims[0]],
//                 [boxDims[2], boxDims[0], boxDims[1]],
//                 [boxDims[2], boxDims[1], boxDims[0]],
//             ];

//             return permutations.some(([l, w, h]) =>
//                 l >= item.l && w >= item.w && h >= item.h
//             );
//         }
//         function findCombinedBoxes(item, boxes) {
//     for (let i = 0; i < boxes.length; i++) {
//         for (let j = i + 1; j < boxes.length; j++) {
//             const b1 = boxes[i].dims;
//             const b2 = boxes[j].dims;

//             // length combine
//             let c1 = {
//                 l: b1.l + b2.l,
//                 w: Math.min(b1.w, b2.w),
//                 h: Math.min(b1.h, b2.h),
//             };

//             if (canFit(item, c1)) {
//                 return [boxes[i], boxes[j]];
//             }

//             // width combine
//             let c2 = {
//                 l: Math.min(b1.l, b2.l),
//                 w: b1.w + b2.w,
//                 h: Math.min(b1.h, b2.h),
//             };

//             if (canFit(item, c2)) {
//                 return [boxes[i], boxes[j]];
//             }

//             // height combine
//             let c3 = {
//                 l: Math.min(b1.l, b2.l),
//                 w: Math.min(b1.w, b2.w),
//                 h: b1.h + b2.h,
//             };

//             if (canFit(item, c3)) {
//                 return [boxes[i], boxes[j]];
//             }
//         }

//     }

//     return null;
// }

//         const suitableBoxes = BoxSize.value
//             .map(box => {
//                 if (!box.value) return null;

//                 const b = parseSize(box.value);
//                 if (!b) return null;

//                 return canFit(finalDims, b) ? { ...box, dims: b } : null;
//             })
//             .filter(Boolean);
//  //Changes 
//         // ❌ No box found
//        if (!suitableBoxes.length) {
//     console.warn("No single box found, trying combination...");

//     const combined = findCombinedBoxes(finalDims, BoxSize.value
//         .map(box => {
//             if (!box.value) return null;
//             const b = parseSize(box.value);
//             return b ? { ...box, dims: b } : null;
//         })
//         .filter(Boolean)
//     );

// if (combined) {
//     console.log("✅ Combined boxes found:", combined);

//     packingResult.value = {
//         type: "combined",
//         boxes: combined.map(b => ({
//             name: b.value,
//             dims: b.dims
//         })),
//         item: finalDims
//     };

//     bestBox.value = {
//         value: combined.map(b => b.value).join(" + "),
//         title: "Combined Box"
//     };

//     return;
// }

//     console.warn("❌ Even combination failed");
//     return;
// }
//                 // ✅ Pick SMALLEST box (best fit)
//         bestBox.value = suitableBoxes.sort((a, b) => {
//             const volA = a.dims.l * a.dims.w * a.dims.h;
//             const volB = b.dims.l * b.dims.w * b.dims.h;
//             return volA - volB;
//         })[0];
//         packingResult.value = {
//             type: "single",
//             box: bestBox.value,
//             item: finalDims
//         };
//         console.log("✅ Selected Box:", bestBox.value.value);
//         const dim = response.data?.quote.items[0]?.dimensions;


//         // console.log("deminsions:", response.data?.quote.items[0]?.dimensions);
//         const padded = getPaddedDimensions(dim);

//         // console.log("Original:", padded.originalWidth, padded.originalHeight);
//         console.log("After Padding:", padded.paddedWidth, padded.paddedHeight);
//         qoutationData.value = response.data?.quote;

//         if (response.data.quote?.shipping_option != null && response.data.quote?.shipping_option != 0) {
//             shippingCost.value = response.data.quote?.shipping_option;
//         } else {
//             shippingCost.value = 0;
//         }
//         cms_processing_fee.value = response.data.config?.cms_fee || 0;
//         // pickupCharge.value = response.data.config?.pickup_charge || 0;
//         if (response.data.quote.is_shipping_option_code != null) {
//             selectedShippingCode.value = response.data.quote.is_shipping_option_code;
//         }
//         if (response.data.quote?.pickupCharge) {
//             pickupCharge.value = response.data.quote?.pickupCharge || 0;
//         }
//         if (response.data.quote?.signatureCost != null && response.data.quote?.signatureCost != 0) {
//             signatureCost.value = response.data.quote.signatureCost
//         }
//         if (response.data.quote?.insuranceCost != null && response.data.quote?.insuranceCost.toString() !== "0") {
//             insuranceCost.value = response.data.quote.insuranceCost
//         }
//         qoutationItems.value = response.data.quote?.items;
//         boxesCount.value = response.data?.boxes || [];
//         if (response.data.boxes != null && response.data.boxes.length > 0) {
//             fields.value = response.data.boxes.map(item => ({
//                 type: item.type,
//                 size: item.size,
//                 confirm_size: item.confirm_size,
//                 weight: item.weight,
//                 subBox: item.subBox ? { type: item.subBox?.type ?? 'Standard', size: item.subBox.size, weight: item.subBox.weight ?? 0 } : null
//             }));
//             fields_test.value = response.data.boxes.map(item => ({
//                 type: "Standard",
//                 size: item.size,
//                 confirm_size: item.confirm_size,
//                 weight: item.weight,
//                 subBox: item.subBox
//                     ? {
//                         type: item.subBox?.type ?? "Standard",
//                         size: item.subBox?.size ?? '',
//                         weight: item.subBox?.weight ?? 0
//                     }
//                     : null
//             }));
//             // Auto-apply box size
//             if (fields_test.value.length) {
//                 setTimeout(() => applyAutomaticBox(), 100); // Ensure reactive update
//             }
//             boxCost.value = response.data.total_box_price;
//             packagingCost.value = response.data.total_packaging_cost;
//             showModal.value = true;
//             showPrizeCard.value = true;

//         } else {
//             showPrizeCard.value = false;
//         }
//         isGetQuote.value = false;
//         if (response.data.shippingRates?.length > 0) {
//             prizes.value = response.data.shippingRates.map((item) => {
//                 return { label: item.Service, value: item.Rate, code: item.Code, insurance: item.InsurancePrice, signature: item.SignaturePrice, baseServiceCharge: item.BaseServiceCharge, allCharges: item.AllCharges || [] };
//             });
//         }
//     }).catch((error) => {
//         isGetQuote.value = false;
//         console.error(error);
//     });
// }
// let hasAutoSyncedPrice = false;
const getSingleQuote = () => {
    axios.post("api/get-single-qoute-test", { id: decodeURIComponent(atob(route.params.id)) }).then((response) => {
        console.log("All Data", response.data);
        const rawItemsData = response.data?.quote?.testItems || response.data?.quote?.test_items || [];
        const items = [];
        let totalItems = 0;
        rawItemsData.forEach(item => {
            let qty = parseInt(item.quantity) || 1;
            // Cap quantity to prevent performance degradation (O(N^2) packing logic freezing the UI)
            if (qty > 100) qty = 100;
            if (totalItems + qty > 200) {
                qty = Math.max(0, 200 - totalItems);
            }
            if (qty <= 0) return;
            
            let extractedDims = [];
            // Check if notes contains dimensions to override the default dimensions
            if (item.notes) {
                const regex3D = /(\d+(?:\.\d+)?)\s*(?:x|×)\s*(\d+(?:\.\d+)?)\s*(?:x|×)\s*(\d+(?:\.\d+)?)/gi;
                const matches3D = Array.from(item.notes.matchAll(regex3D));
                if (matches3D.length > 0) {
                    extractedDims = matches3D.map(m => `${m[1]} w x ${m[2]} h x ${m[3]} d inches`);
                } else {
                    const regex2D = /(\d+(?:\.\d+)?)\s*(?:x|×)\s*(\d+(?:\.\d+)?)/gi;
                    const matches2D = Array.from(item.notes.matchAll(regex2D));
                    if (matches2D.length > 0) {
                        extractedDims = matches2D.map(m => `${m[1]} w x ${m[2]} h inches`);
                    }
                }
            }

            if (extractedDims.length > 1) {
                // Multiple dimensions found in notes, create a row for each distinct dimension found
                for (let i = 0; i < extractedDims.length; i++) {
                    items.push({ 
                        ...item, 
                        dimensions: extractedDims[i],
                        item_name: item.item_name || 'Item',
                        lot_number: i === 0 ? item.lot_number : '',
                        quantity: i === 0 ? item.quantity : '',
                        lot_price: i === 0 ? item.lot_price : '',
                        is_duplicate_display: false // different sizes, so display all
                    });
                }
                totalItems += extractedDims.length;
            } else {
                if (extractedDims.length === 1) {
                    const originalHasLabels = item.dimensions && /[wdh]\b/i.test(item.dimensions.replace(/inches|inch/gi, ''));
                    if (!originalHasLabels) {
                        item.dimensions = extractedDims[0];
                    } else {
                        console.log(`Preserving original explicit dimensions: ${item.dimensions} over notes: ${extractedDims[0]}`);
                    }
                }
                for (let i = 0; i < qty; i++) {
                    items.push({ 
                        ...item, 
                        item_name: item.item_name || 'Item',
                        lot_number: i === 0 ? item.lot_number : '',
                        quantity: i === 0 ? item.quantity : '',
                        lot_price: i === 0 ? item.lot_price : '',
                        is_duplicate_display: i > 0 // same name/size, only show first
                    });
                }
                totalItems += qty;
            }
        });
        console.log("Items Array (Expanded by Quantity):", items);
        insurance_per_box.value = parseFloat(response.data.insurance_per_box || 0);
        insurance_price.value = parseFloat(response.data.insurance_price || 0);
        console.log("Insurance Per Box:", insurance_per_box.value, "Insurance Price:", insurance_price.value);
       
        amount_exceeds_limit.value = response.data.amount_exceeds_limit || false;
        max_tier_value.value = response.data.max_tier_value || 0;

        // Define padding constant
        const PADDING = 4; // 4 inches total padding (2 inches on each side)

        const findTightFitBox = (item, boxes) => {
            if (!item) return '';
            const itemDims = [Math.ceil(item.l), Math.ceil(item.h), Math.ceil(item.w)];

            let bestMatch = '';
            let bestScore = Infinity;
            let bestDepthDiff = Infinity;
            
            let fallbackMatch = '';
            let fallbackBestScore = Infinity;
            let fallbackBestDepthDiff = Infinity;

            for (const box of boxes) {
                const parsed = parseBoxSize(box.value);
                if (!parsed) continue;
                
                const boxDims = [parsed.l, parsed.h, parsed.w];
                const fit = evaluateBoxFit(itemDims, boxDims);
                
                if (fit.isValid) {
                    if (fit.score < bestScore || (fit.score === bestScore && fit.depthDiff < bestDepthDiff)) {
                        bestScore = fit.score;
                        bestDepthDiff = fit.depthDiff;
                        bestMatch = box.value;
                    }
                }
                
                if (fit.isFallbackValid) {
                    if (fit.score < fallbackBestScore || (fit.score === fallbackBestScore && fit.depthDiff < fallbackBestDepthDiff)) {
                        fallbackBestScore = fit.score;
                        fallbackBestDepthDiff = fit.depthDiff;
                        fallbackMatch = box.value;
                    }
                }
            }
            const finalMatch = bestMatch || fallbackMatch;
            console.log(`🎯 Tight fit for item dims ${itemDims.join('x')} → ${finalMatch}`);
            return {
                box: finalMatch,
                dims: itemDims.join('x')
            };
        };

        // ========== ITEM PARSER (RAW DIMENSIONS) ==========
        function extractRawDimensions(size) {
            if (!size) return null;
            const value = size.toLowerCase().replace(/×/g, 'x').replace(/\s+/g, ' ').trim();
            const wMatch = value.match(/(\d+(?:\.\d+)?)\s*w/i);
            const hMatch = value.match(/(\d+(?:\.\d+)?)\s*h/i);
            const dMatch = value.match(/(\d+(?:\.\d+)?)\s*d/i);
            const lMatch = value.match(/(\d+(?:\.\d+)?)\s*l/i);
            
            const hasExplicitLabels = !!(wMatch || hMatch || dMatch || lMatch);
            
            let width = wMatch ? parseFloat(wMatch[1]) : null;
            let height = hMatch ? parseFloat(hMatch[1]) : null;
            let depth = dMatch ? parseFloat(dMatch[1]) : null;
            let length = lMatch ? parseFloat(lMatch[1]) : null;
            const numbers = value.match(/\d+(?:\.\d+)?/g);
            if (width === null && height === null && numbers) {
                if (numbers.length === 2) {
                    width = parseFloat(numbers[0]);
                    depth = 2;
                    height = parseFloat(numbers[1]);
                } else if (numbers.length >= 3) {
                    width = parseFloat(numbers[0]);
                    depth = parseFloat(numbers[1]);
                    height = parseFloat(numbers[2]);
                } else if (numbers.length === 1) {
                    width = parseFloat(numbers[0]);
                    depth = 2;
                    height = parseFloat(numbers[0]);
                }
            }
            if (width === null && length !== null) width = length;
            if (width === null) width = 3; // default width if not provided
            if (depth === null) depth = 2;
            if (height === null) height = width; // fallback
            
            return { w: width, d: depth, h: height, hasExplicitLabels };
        }

        let allItems = [];
        items.forEach((item, index) => {
            const simpleSizeStr = item.size || item.dimensions;
            const notesStr = item.notes || '';
            
            let finalRawDims = extractRawDimensions(simpleSizeStr);
            const noteDims = extractRawDimensions(notesStr);
            
            if (finalRawDims && noteDims) {
                // If only one has explicit labels, prioritize that one
                if (finalRawDims.hasExplicitLabels && !noteDims.hasExplicitLabels) {
                    console.log(`Item ${index + 1}: Simple dims have explicit labels, notes do not. Prioritizing simple dims.`);
                } else if (!finalRawDims.hasExplicitLabels && noteDims.hasExplicitLabels) {
                    console.log(`Item ${index + 1}: Note dims have explicit labels, simple do not. Prioritizing note dims.`);
                    finalRawDims = noteDims;
                } else {
                    // Both have explicit labels OR both lack explicit labels -> If values differ, prioritize notes
                    if (finalRawDims.w !== noteDims.w || finalRawDims.d !== noteDims.d || finalRawDims.h !== noteDims.h) {
                        console.log(`Item ${index + 1}: Note dims differ from simple dims. Prioritizing note dims.`);
                        finalRawDims = noteDims;
                    }
                }
            } else if (!finalRawDims && noteDims) {
                finalRawDims = noteDims;
            }
            
            if (!finalRawDims) {
                console.warn(`Item ${index + 1} missing size in both simple and notes`);
                return;
            }

            const lowerNotes = notesStr.toLowerCase();
            const isUnframed = lowerNotes.includes('unframe') || lowerNotes.includes('un-framed') || lowerNotes.includes('rolled');
            const isFramed = !isUnframed && (lowerNotes.includes('framed') || lowerNotes.includes('frame'));

            let isFragile = false;
            if (isFramed || lowerNotes.includes('article') || lowerNotes.includes('glass')) {
                isFragile = true;
            }

            // Fragile: padding value changed from 5 to 3
            const paddingValue = isFragile ? 4 : PADDING;
            const isStandard = !isFragile;

            // Apply padding according to Standard vs Fragile vs Unframed rules
            const l = finalRawDims.w + paddingValue; 
            const w = finalRawDims.h + paddingValue; 
            
            let h = finalRawDims.d;
            if (isUnframed) {
                // Simon's Rule: Flat mailer is NOT used. Slim picture BOX is used (minimum depth = 6 inches fix).
                h = Math.max(6, Math.ceil(h + paddingValue));
            } else if (isStandard) {
                h += paddingValue; // Standard gets padding on depth
            } else if (isFragile && h === 2) {
                h = 3; // Fragile gets depth rounded to 3
            }
            
            const result = { l: l, h: h, w: w, isUnframed: isUnframed };
            console.log(`Item ${index + 1} padded: ${result.l}×${result.h}×${result.w} (Fragile: ${isFragile}, Unframed: ${isUnframed})`);
            allItems.push(result);
        });

        if (!allItems.length) {
            console.warn("No valid items found");
            packingResult.value = null;
            bestBox.value = { value: '', title: '' };
            // DO NOT return here, otherwise quote data won't be set!
        }

        // ========== FUNCTION TO CALCULATE TOTAL DIMENSIONS ==========
        const calculateTotalDimensions = (items, paddingAmount = PADDING) => {
            if (!items || items.length === 0) return null;
            let totalLength = 0;
            let maxWidth = 0;
            let maxHeight = 0;
            
            const unframedList = items.filter(i => i.isUnframed);
            const otherList = items.filter(i => !i.isUnframed);
            
            let combinedDepth = 0;
            if (otherList.length > 0) {
                const otherSum = otherList.reduce((sum, item) => sum + (item.h || 0), 0);
                combinedDepth = otherSum + (unframedList.length * 0.5);
            } else if (unframedList.length > 0) {
                combinedDepth = Math.max(6, unframedList.reduce((max, item) => Math.max(max, item.h || 0), 0));
            }
            
            items.forEach(item => {
                totalLength += item.l;
                maxWidth = Math.max(maxWidth, item.w);
                maxHeight = Math.max(maxHeight, item.h);
            });
            const finalL = Math.ceil(totalLength + paddingAmount);
            const finalW = Math.ceil(maxWidth + paddingAmount);
            const effectiveDepth = combinedDepth > 0 ? combinedDepth : maxHeight;
            const finalH = Math.max(6, Math.ceil(effectiveDepth + (unframedList.length === items.length ? 0 : paddingAmount)));
            return { l: finalL, w: finalW, h: finalH, formatted: `${finalL}x${finalH}x${finalW}` };
        };

        // Helper: Get all rotations
        function getAllRotations(item) {
            return [
                { l: item.l, w: item.w, h: item.h },
                { l: item.l, w: item.h, h: item.w },
                { l: item.w, w: item.l, h: item.h },
                { l: item.w, w: item.h, h: item.l },
                { l: item.h, w: item.l, h: item.w },
                { l: item.h, w: item.w, h: item.l }
            ];
        }

        function canSingleItemFit(item, box) {
            if (!item || !box) return false;
            const rotations = getAllRotations(item);
            return rotations.some(rot => rot.l <= box.l && rot.w <= box.w && rot.h <= box.h);
        }

        function canMultipleItemsFit(items, box) {
            if (!items || items.length === 0) return false;
            if (items.length === 1) return canSingleItemFit(items[0], box);
            if (items.length === 2) {
                const item1 = items[0], item2 = items[1];
                const rotations1 = getAllRotations(item1);
                const rotations2 = getAllRotations(item2);
                for (const rot1 of rotations1) {
                    for (const rot2 of rotations2) {
                        const sideBySideL = { l: rot1.l + rot2.l, w: Math.max(rot1.w, rot2.w), h: Math.max(rot1.h, rot2.h) };
                        if (sideBySideL.l <= box.l && sideBySideL.w <= box.w && sideBySideL.h <= box.h) return true;
                        const sideBySideW = { l: Math.max(rot1.l, rot2.l), w: rot1.w + rot2.w, h: Math.max(rot1.h, rot2.h) };
                        if (sideBySideW.l <= box.l && sideBySideW.w <= box.w && sideBySideW.h <= box.h) return true;
                        const stacked = { l: Math.max(rot1.l, rot2.l), w: Math.max(rot1.w, rot2.w), h: rot1.h + rot2.h };
                        if (stacked.l <= box.l && stacked.w <= box.w && stacked.h <= box.h) return true;
                    }
                }
            }
            if (items.length >= 3) {
                const totalVolume = items.reduce((sum, item) => sum + (item.l * item.w * item.h), 0);
                const boxVolume = box.l * box.w * box.h;
                if (boxVolume < totalVolume * 1.3) return false;
                const maxL = Math.max(...items.map(i => i.l));
                const maxW = Math.max(...items.map(i => i.w));
                const totalH = items.reduce((sum, i) => sum + i.h, 0);
                if (maxL <= box.l && maxW <= box.w && totalH <= box.h) return true;
                const totalL = items.reduce((sum, i) => sum + i.l, 0);
                const maxH = Math.max(...items.map(i => i.h));
                if (totalL <= box.l && maxW <= box.w && maxH <= box.h) return true;
            }
            return false;
        }

        // findNearestBox removed — now using findClosestBoxSize with evaluateBoxFit for multi-item


        function findBestBoxForItems(items, availableBoxes) {
            let bestBoxMatch = null;
            let smallestVolume = Infinity;
            let bestDepthDiff = Infinity;
            
            let fallbackMatch = null;
            let fallbackMinVolume = Infinity;
            let fallbackBestDepthDiff = Infinity;

            const itemsVolume = items.reduce((sum, item) => sum + (item.w  * item.l * item.h), 0);
            
            // First pass: Standard boxes
            for (const box of availableBoxes) {
                if (!box.value) continue;
                const boxDims = parseBoxSize(box.value);
                if (!boxDims) continue;

                if (items.length === 1) {
                    const itemDims = [Math.ceil(items[0].l), Math.ceil(items[0].h), Math.ceil(items[0].w)];
                    const bDims = [boxDims.l, boxDims.h, boxDims.w];
                    const fit = evaluateBoxFit(itemDims, bDims);
                    
                    if (fit.isValid) {
                        const waste = ((fit.volume - itemsVolume) / fit.volume) * 100;
                        if (waste <= 20 && (fit.volume < smallestVolume || (fit.volume === smallestVolume && fit.depthDiff < bestDepthDiff))) {
                            smallestVolume = fit.volume;
                            bestDepthDiff = fit.depthDiff;
                            bestBoxMatch = { ...box, dims: boxDims };
                            console.log(`✓ ${box.value} strictly fits! Volume: ${fit.volume}, Waste: ${waste.toFixed(1)}%`);
                        }
                    }
                    if (fit.isFallbackValid) {
                        const waste = ((fit.volume - itemsVolume) / fit.volume) * 100;
                        if (waste <= 20 && (fit.volume < fallbackMinVolume || (fit.volume === fallbackMinVolume && fit.depthDiff < fallbackBestDepthDiff))) {
                            fallbackMinVolume = fit.volume;
                            fallbackBestDepthDiff = fit.depthDiff;
                            fallbackMatch = { ...box, dims: boxDims };
                        } else if (fit.volume < fallbackMinVolume || (fit.volume === fallbackMinVolume && fit.depthDiff < fallbackBestDepthDiff)) {
                            // Save absolute fallback even if waste > 20%
                            fallbackMinVolume = fit.volume;
                            fallbackBestDepthDiff = fit.depthDiff;
                            fallbackMatch = { ...box, dims: boxDims };
                        }
                    }
                } else {
                    if (canMultipleItemsFit(items, boxDims)) {
                        const boxVolume = boxDims.w * boxDims.l * boxDims.h;
                        const waste = ((boxVolume - itemsVolume) / boxVolume) * 100;
                        if (waste <= 20 && boxVolume < smallestVolume) {
                            smallestVolume = boxVolume;
                            bestBoxMatch = { ...box, dims: boxDims };
                            console.log(`✓ ${box.value} fits multiple! Volume: ${boxVolume}, Waste: ${waste.toFixed(1)}%`);
                        }
                        // Also track fallback for multiple items
                        if (boxVolume < fallbackMinVolume) {
                            fallbackMinVolume = boxVolume;
                            fallbackMatch = { ...box, dims: boxDims };
                        }
                    }
                }
            }
            
            // If no standard box passes the 20% waste rule, trigger TELESCOPING
            if (!bestBoxMatch) {
                console.log(`⚠️ No standard box passed 20% waste rule. Triggering TELESCOPING.`);
                let bestTelescopedBox = null;
                let smallestTelescopedVolume = Infinity;

                for (const box of availableBoxes) {
                    if (!box.value) continue;
                    const baseBoxDims = parseBoxSize(box.value);
                    if (!baseBoxDims) continue;

                    // Telescope: double the height, keep width and depth fixed
                    const telescopedDims = { l: baseBoxDims.l, w: baseBoxDims.w, h: baseBoxDims.h * 2 };
                    const telescopedVolume = telescopedDims.l * telescopedDims.w * telescopedDims.h;

                    if (items.length === 1) {
                        const itemDims = [Math.ceil(items[0].l), Math.ceil(items[0].h), Math.ceil(items[0].w)];
                        const bDims = [telescopedDims.l, telescopedDims.h, telescopedDims.w];
                        const fit = evaluateBoxFit(itemDims, bDims);

                        if (fit.isValid || fit.isFallbackValid) {
                            const actualVolume = fit.isValid ? fit.volume : telescopedVolume;
                            if (actualVolume < smallestTelescopedVolume) {
                                smallestTelescopedVolume = actualVolume;
                                bestTelescopedBox = { 
                                    ...box, 
                                    dims: telescopedDims, 
                                    value: `${box.value} (Telescoped)`,
                                    title: `${box.title} (Telescoped - Uses 2 Boxes)`
                                };
                            }
                        }
                    } else {
                        if (canMultipleItemsFit(items, telescopedDims)) {
                            if (telescopedVolume < smallestTelescopedVolume) {
                                smallestTelescopedVolume = telescopedVolume;
                                bestTelescopedBox = { 
                                    ...box, 
                                    dims: telescopedDims, 
                                    value: `${box.value} (Telescoped)`,
                                    title: `${box.title} (Telescoped - Uses 2 Boxes)`
                                };
                            }
                        }
                    }
                }

                if (bestTelescopedBox) {
                    console.log(`✓ Found telescoped box: ${bestTelescopedBox.value}`);
                    bestBoxMatch = bestTelescopedBox;
                }
            }
            
            // Absolute fallback if neither standard (<= 20% waste) nor telescoped fit
            if (!bestBoxMatch && fallbackMatch) {
                console.log(`⚠️ No telescoped box fit either. Using fallback (ignores waste): ${fallbackMatch.value}`);
                bestBoxMatch = fallbackMatch;
            }
            
            return bestBoxMatch;
        }

        function packItemsIntoBoxes(items, availableBoxes) {
            const sortedItems = [...items].sort((a, b) => (b.l * b.w * b.h) - (a.l * a.w * a.h));
            const usedBoxes = [];
            let remainingItems = [...sortedItems];
            
            while (remainingItems.length > 0) {
                let bestBoxForGroup = null;
                let bestGroupSize = 0;
                
                // OPTIMIZATION: Loop forwards. If `i` items don't fit, `i+1` items won't fit either.
                // This reduces time complexity from O(N^3) to O(N), preventing browser freezes.
                for (let i = 1; i <= remainingItems.length; i++) {
                    const testGroup = remainingItems.slice(0, i);
                    const box = findBestBoxForItems(testGroup, availableBoxes);
                    if (box) {
                        bestBoxForGroup = box;
                        bestGroupSize = i;
                    } else {
                        // Stop testing larger groups; they definitely won't fit if this group didn't.
                        break;
                    }
                }
                
                if (bestBoxForGroup) {
                    usedBoxes.push(bestBoxForGroup);
                    remainingItems = remainingItems.slice(bestGroupSize);
                } else {
                    // Fallback if even 1 item didn't fit into any box
                    const fallbackBox = findBestBoxForItems([remainingItems[0]], availableBoxes);
                    if (fallbackBox) {
                        usedBoxes.push(fallbackBox);
                    }
                    remainingItems = remainingItems.slice(1);
                }
            }
            return usedBoxes;
        }

        // ========== MAIN LOGIC ==========
        let bestSingleBox = findBestBoxForItems(allItems, BoxSize.value);

        if (bestSingleBox) {
            packingResult.value = { type: "single", box: bestSingleBox, items: allItems };
            bestBox.value = { value: bestSingleBox.value, title: bestSingleBox.title };
            console.log("✅ Single box:", bestSingleBox.value);
        } else if (allItems.length > 1) {
            // For multiple items: parse RAW dimensions (no per-item padding), combine, then add 2 inches padding once
            const rawItems = [];
            items.forEach((item) => {
                const size = item.size || item.dimensions;
                if (!size) return;
                const value = size.toLowerCase().replace(/×/g, 'x').replace(/\s+/g, ' ').trim();
                const wMatch = value.match(/(\d+(?:\.\d+)?)\s*w/i);
                const hMatch = value.match(/(\d+(?:\.\d+)?)\s*h/i);
                const dMatch = value.match(/(\d+(?:\.\d+)?)\s*d/i);
                const lMatch = value.match(/(\d+(?:\.\d+)?)\s*l/i);
                let width = wMatch ? parseFloat(wMatch[1]) : null;
                let height = hMatch ? parseFloat(hMatch[1]) : null;
                let depth = dMatch ? parseFloat(dMatch[1]) : null;
                if (lMatch && width === null) width = parseFloat(lMatch[1]);
                const numbers = value.match(/\d+(?:\.\d+)?/g);
                if (width === null && height === null && numbers) {
                    if (numbers.length === 2) {
                        width = parseFloat(numbers[0]);
                        height = parseFloat(numbers[1]);
                    } else if (numbers.length >= 3) {
                        width = parseFloat(numbers[0]);
                        depth = parseFloat(numbers[1]);
                        height = parseFloat(numbers[2]);
                    } else if (numbers.length === 1) {
                        width = parseFloat(numbers[0]);
                        height = parseFloat(numbers[0]);
                    }
                }
                
                const itemNameLower = (item.item_name || item.name || "").toLowerCase();
                const notesLower = (item.notes || item.item_notes || item.description || item.ItemNotes || "").toLowerCase();
                const categoryLower = (item.category_name || item.category || item.item_category || item.type || item.item_type || "").toLowerCase();
                const isUnframedPicture = itemNameLower.includes("unframe") || notesLower.includes("unframe") || categoryLower.includes("unframe");
                
                if (width === null) width = 3; // default width if not provided
                if (depth === null) depth = 2;
                if (width !== null && height !== null) {
                    rawItems.push({ l: width, h: depth, w: height, isUnframed: isUnframedPicture });
                }
            });

            if (rawItems.length > 0) {
                // Combine raw dimensions, then add only 2 inches padding to combined total
                const combinedWithPadding = calculateTotalDimensions(rawItems, 2);
                console.log(`📦 Multiple items combined raw + 2in padding: ${combinedWithPadding.formatted}`);
                
                // Find best box from list using evaluateBoxFit rules
                const combinedBoxSize = findClosestBoxSize(combinedWithPadding.formatted);
                if (combinedBoxSize) {
                    const matchedBox = BoxSize.value.find(b => b.value === combinedBoxSize);
                    packingResult.value = { type: "single", box: matchedBox || { value: combinedBoxSize }, items: allItems };
                    bestBox.value = { value: combinedBoxSize, title: matchedBox ? matchedBox.title : combinedBoxSize };
                    console.log(`✅ Combined best box: ${combinedBoxSize}`);
                } else {
                    console.warn("No single box for combined items, trying multiple boxes...");
                    const packedBoxes = packItemsIntoBoxes(allItems, BoxSize.value);
                    if (packedBoxes && packedBoxes.length >= 1) {
                        packingResult.value = { type: "combined", boxes: packedBoxes.map(b => ({ name: b.value, dims: b.dims })), items: allItems };
                        bestBox.value = { value: packedBoxes.map(b => b.value).join(" + "), title: "Multiple Boxes" };
                        console.log("✅ Multiple boxes:", packedBoxes);
                    } else {
                        packingResult.value = null;
                        bestBox.value = { value: '', title: '' };
                    }
                }
            }
        }

        const totalDimensions = calculateTotalDimensions(allItems);
        console.log("📦 Total Dimensions (with padding):", totalDimensions);

        let recommendedBoxSize = '';
        let recommendedBoxDims = '';
        if (allItems.length === 1) {
            const tightFitResult = findTightFitBox(allItems[0], BoxSize.value);
            recommendedBoxSize = tightFitResult.box;
            recommendedBoxDims = tightFitResult.dims;
        } else if (allItems.length > 1) {
            // Use raw dimensions + 2 inches for recommendation (same as box selection)
            const rawForRecommend = [];
            items.forEach((item) => {
                const size = item.size || item.dimensions;
                if (!size) return;
                const value = size.toLowerCase().replace(/×/g, 'x').replace(/\s+/g, ' ').trim();
                const wMatch = value.match(/(\d+(?:\.\d+)?)\s*w/i);
                const hMatch = value.match(/(\d+(?:\.\d+)?)\s*h/i);
                const dMatch = value.match(/(\d+(?:\.\d+)?)\s*d/i);
                const lMatch = value.match(/(\d+(?:\.\d+)?)\s*l/i);
                let width = wMatch ? parseFloat(wMatch[1]) : null;
                let height = hMatch ? parseFloat(hMatch[1]) : null;
                let depth = dMatch ? parseFloat(dMatch[1]) : null;
                if (lMatch && width === null) width = parseFloat(lMatch[1]);
                const numbers = value.match(/\d+(?:\.\d+)?/g);
                if (width === null && height === null && numbers) {
                    if (numbers.length === 2) { width = parseFloat(numbers[0]); height = parseFloat(numbers[1]); }
                    else if (numbers.length >= 3) { width = parseFloat(numbers[0]); depth = parseFloat(numbers[1]); height = parseFloat(numbers[2]); }
                    else if (numbers.length === 1) { width = parseFloat(numbers[0]); height = parseFloat(numbers[0]); }
                }
                
                const itemNameLower = (item.item_name || item.name || "").toLowerCase();
                const notesLower = (item.notes || item.item_notes || item.description || item.ItemNotes || "").toLowerCase();
                const categoryLower = (item.category_name || item.category || item.item_category || item.type || item.item_type || "").toLowerCase();
                const isUnframedPicture = itemNameLower.includes("unframe") || notesLower.includes("unframe") || categoryLower.includes("unframe");

                if (width === null) width = 3; // default width if not provided
                if (depth === null) depth = isUnframedPicture ? 6 : 2;
                if (width !== null && height !== null) rawForRecommend.push({ l: width, h: depth, w: height, isUnframed: isUnframedPicture });
            });
            if (rawForRecommend.length > 0) {
                const maxW = Math.ceil(Math.max(...rawForRecommend.map(d => d.l)) + 2);
                const maxH = Math.ceil(Math.max(...rawForRecommend.map(d => d.w)) + 2);
                
                const unframedRecommend = rawForRecommend.filter(d => d.isUnframed);
                const otherRecommend = rawForRecommend.filter(d => !d.isUnframed);
                
                let combinedDepth = 0;
                if (otherRecommend.length > 0) {
                    const otherDepthSum = otherRecommend.reduce((sum, d) => sum + d.h, 0);
                    combinedDepth = otherDepthSum + (unframedRecommend.length * 0.5);
                } else if (unframedRecommend.length > 0) {
                    const maxItemD = Math.max(...unframedRecommend.map(d => d.h));
                    combinedDepth = maxItemD + ((unframedRecommend.length - 1) * 0.5);
                }
                let totalD;
                if (unframedRecommend.length > 0 && otherRecommend.length === 0) {
                    if (combinedDepth <= 6) {
                        totalD = 6;
                    } else if (combinedDepth <= 8) {
                        totalD = 8;
                    } else {
                        totalD = Math.ceil(combinedDepth + 4);
                    }
                } else {
                    totalD = combinedDepth <= 1 ? Math.max(1, Math.ceil(combinedDepth)) : Math.ceil(combinedDepth + 2);
                }
                recommendedBoxSize = findClosestBoxSize(`${maxW}x${totalD}x${maxH}`, unframedRecommend.length > 0);
                console.log(`📦 Multi-item raw combined: ${maxW}x${totalD}x${maxH}`);
            }
        }
        console.log("✅ Recommended Box:", recommendedBoxSize);

        qoutationData.value = response.data?.quote;

        // Fix corrupted address data: font ligature converts 'tt' → © on save.
        // \s*©\s* also removes any spaces around © so "Ba © ery" → "Battery" (no spaces).
        const fixLigature = (str) => typeof str === 'string' ? str.replace(/\s*©\s*/g, 'tt') : str;
        if (qoutationData.value) {
            qoutationData.value.ship_to_address = fixLigature(qoutationData.value.ship_to_address);
            qoutationData.value.ship_to_cityzip = fixLigature(qoutationData.value.ship_to_cityzip);
            qoutationData.value.ship_to_country  = fixLigature(qoutationData.value.ship_to_country);
        }
        const extractedBoxCost = response.data?.total_box_price ?? response.data?.quote?.total_box_price ?? response.data?.quote?.box_cost ?? response.data?.quote?.boxCost;
        const extractedPkgCost = response.data?.total_packaging_cost ?? response.data?.quote?.total_packaging_cost ?? response.data?.quote?.packaging_cost ?? response.data?.quote?.packagingCost;
        
        if (extractedBoxCost !== undefined && extractedBoxCost !== null) {
            boxCost.value = extractedBoxCost;
        }
        if (extractedPkgCost !== undefined && extractedPkgCost !== null) {
            packagingCost.value = extractedPkgCost;
        }
        
        shippingCost.value = (response.data.quote?.shipping_option != null && response.data.quote?.shipping_option != 0) ? response.data.quote?.shipping_option : 0;
        cms_processing_fee.value = response.data.config?.cms_fee || 0;
        insurance_domestic.value = response.data.config?.insurance_domestic || 0;
        console.log("Insurance Costs:", insurance_domestic.value, insurance_international.value);

        insurance_international.value = response.data.config?.insurance_international || 0;
        console.log("Insurance Costs:", insurance_domestic.value, insurance_international.value);
        if (response.data.insurance_per_box != null) insurance_per_box.value = parseFloat(response.data.insurance_per_box);
        if (response.data.insurance_price != null) insurance_price.value = parseFloat(response.data.insurance_price);
        if (response.data.signature_per_box != null) signature_per_box.value = parseFloat(response.data.signature_per_box);
        if (response.data.config?.signature_domestic != null) signature_domestic.value = parseFloat(response.data.config.signature_domestic);
        if (response.data.config?.signature_international != null) signature_international.value = parseFloat(response.data.config.signature_international);
        if (response.data.quote.is_shipping_option_code != null) selectedShippingCode.value = response.data.quote.is_shipping_option_code;
        if (response.data.quote?.pickupCharge) pickupCharge.value = response.data.quote?.pickupCharge || 0;
        if (response.data.quote?.signatureCost != null && response.data.quote?.signatureCost != 0) signatureCost.value = response.data.quote.signatureCost;
        if (response.data.quote?.insuranceCost != null && response.data.quote?.insuranceCost.toString() !== "0") insuranceCost.value = response.data.quote.insuranceCost;
        console.log("insuranceCost value :", insuranceCost.value);
        qoutationItems.value = items;
        boxesCount.value = response.data?.boxes || [];

        if (response.data.boxes != null && response.data.boxes.length > 0) {
            console.log("Boxes from API:", response.data.boxes);
            
            const mergedFields = [];
            const mergedFieldsTest = [];
            
            const getBoxTitle = (val) => {
                if (!val) return '';
                const found = BoxSize.value.find(b => b.value.toLowerCase() === val.toLowerCase());
                return found ? found.title : val;
            };

            let i = 0;
            while (i < response.data.boxes.length) {
                const item = response.data.boxes[i];
                const normalizedSize1 = item.size ? item.size.toLowerCase() : '';
                let cleanedConfirmSize = item.confirm_size || '';
                if (cleanedConfirmSize.includes('Tight fit for item dims')) {
                    cleanedConfirmSize = cleanedConfirmSize.replace('Tight fit for item dims', '').trim();
                }

                // Check how many consecutive boxes share the exact same cleanedConfirmSize
                let j = i + 1;
                while (
                    j < response.data.boxes.length && 
                    cleanedConfirmSize && 
                    (response.data.boxes[j].confirm_size || '').replace('Tight fit for item dims', '').trim() === cleanedConfirmSize
                ) {
                    j++;
                }

                const groupCount = j - i;
                if (groupCount > 1) {
                    // Merge all boxes in this group (2, 3, or more) into a single row
                    const selectedSizes = [];
                    for (let k = i; k < j; k++) {
                        const boxK = response.data.boxes[k];
                        const normSize = boxK.size ? boxK.size.toLowerCase() : '';
                        selectedSizes.push(getBoxTitle(normSize));
                    }

                    let finalConfirmSizePair = cleanedConfirmSize;
                    if (items.length === 1 && recommendedBoxDims && !item.subBox) {
                        finalConfirmSizePair = recommendedBoxDims;
                    }

                    mergedFields.push({
                        type: 'Fragile',
                        size: normalizedSize1,
                        confirm_size: finalConfirmSizePair,
                        weight: item.weight ?? '20',
                        selected_sizes: selectedSizes,
                        subBox: item.subBox ? { type: item.subBox?.type ?? 'Fragile', size: item.subBox.size, weight: item.subBox.weight ?? 0 } : null,
                        is_telescoped: true,
                        primary_box: selectedSizes[0] || '',
                        secondary_box: selectedSizes[1] || ''
                    });

                    const subBoxObj = item.subBox ? {
                        type: item.subBox.type || 'Fragile',
                        size: item.subBox.size || '',
                        weight: item.subBox.weight ?? '20',
                        itemName: 'Combined Items Box',
                        selected_sizes: item.subBox.size ? [getBoxTitle(item.subBox.size)] : [],
                        is_telescoped: false,
                        telescoped_boxes: []
                    } : null;

                    mergedFieldsTest.push({
                        type: 'Fragile',
                        size: normalizedSize1,
                        confirm_size: finalConfirmSizePair,
                        weight: item.weight ?? '20',
                        selected_sizes: selectedSizes,
                        subBox: null,
                        subBoxes: subBoxObj ? [subBoxObj] : null,
                        is_telescoped: true,
                        primary_box: selectedSizes[0] || '',
                        secondary_box: selectedSizes[1] || ''
                    });

                    i = j; // Advance past all merged boxes
                } else {
                    let finalSize = normalizedSize1;
                    let finalConfirmSize = cleanedConfirmSize;

                    // Automatically update the UI to match the newly calculated single box if it differs from the saved one
                    if (items.length === 1 && recommendedBoxSize && !item.subBox) {
                        finalSize = recommendedBoxSize;
                        finalConfirmSize = recommendedBoxDims ? recommendedBoxDims : recommendedBoxSize;
                    }

                    const title1 = finalSize ? getBoxTitle(finalSize) : '';
                    mergedFields.push({
                        type: item.type || 'Fragile',
                        size: finalSize,
                        confirm_size: finalConfirmSize,
                        weight: item.weight ?? '20',
                        selected_sizes: title1 ? [title1] : (finalSize ? [finalSize] : []),
                        subBox: item.subBox ? { type: item.subBox?.type ?? 'Fragile', size: item.subBox.size, weight: item.subBox.weight ?? 0 } : null,
                        is_telescoped: false
                    });
                    
                    const subBoxObj = item.subBox ? {
                        type: item.subBox.type || 'Fragile',
                        size: item.subBox.size || '',
                        weight: item.subBox.weight ?? '20',
                        itemName: 'Combined Items Box',
                        selected_sizes: item.subBox.size ? [getBoxTitle(item.subBox.size)] : [],
                        is_telescoped: false,
                        telescoped_boxes: []
                    } : null;

                    mergedFieldsTest.push({
                        type: item.type || 'Fragile',
                        size: finalSize,
                        confirm_size: finalConfirmSize,
                        weight: item.weight ?? '20',
                        selected_sizes: title1 ? [title1] : (finalSize ? [finalSize] : []),
                        subBox: null,
                        subBoxes: subBoxObj ? [subBoxObj] : null,
                        is_telescoped: false
                    });

                    i++;
                }
            }
            
            fields.value = mergedFields;
            fields_test.value = mergedFieldsTest;
            console.log("Fields test after mapping API boxes:", fields_test.value);
            
            // Ensure fields with loaded database subBoxes automatically compute their padded master outer sizing while preserving non-subbox saved values exactly
            fields_test.value.forEach(field => {
                if (field.subBoxes && field.subBoxes.length > 0) {
                    field.type = 'Fragile';
                    field.is_telescoped = false;
                    updateMainBoxFromSubBoxes(field);
                }
            });

            // Avoid overwriting saved quotation values from the database
            // if (fields_test.value.length && bestBox.value?.value) setTimeout(() => applyAutomaticBox(), 100);
            const extractedBoxCostInner = response.data?.total_box_price ?? response.data?.quote?.total_box_price ?? response.data?.quote?.box_cost ?? response.data?.quote?.boxCost ?? response.data?.quote?.box_price ?? response.data?.quote?.boxPrice;
            const extractedPkgCostInner = response.data?.total_packaging_cost ?? response.data?.quote?.total_packaging_cost ?? response.data?.quote?.packaging_cost ?? response.data?.quote?.packagingCost ?? response.data?.quote?.packaging_price ?? response.data?.quote?.packagingPrice;
            
            if (extractedBoxCostInner !== undefined && extractedBoxCostInner !== null && extractedBoxCostInner.toString() !== "0") {
                boxCost.value = extractedBoxCostInner;
            }
            if (extractedPkgCostInner !== undefined && extractedPkgCostInner !== null && extractedPkgCostInner.toString() !== "0") {
                packagingCost.value = extractedPkgCostInner;
            }
            
            // First time auto save without clicking the get price button
            // if (!hasAutoSyncedPrice && fields.value.some(f => f.size || (Array.isArray(f.selected_sizes) && f.selected_sizes.length > 0))) {
            //     hasAutoSyncedPrice = true;
            //     setTimeout(() => {
            //         console.log("First time auto save without clicking Get Price button...");
            //         submit();
            //     }, 300);
            // }
            
            showModal.value = true;
            showPrizeCard.value = true;
        } else {
            showPrizeCard.value = false;

            if (allItems.length > 0) {



                // ✅ Helper: extract dimensions from notes string (e.g. "24x18", "30w 24h 2d")
                const extractDimsFromNotes = (notesStr) => {
                    if (!notesStr) return null;
                    const n = notesStr.toLowerCase().replace(/×/g, 'x').trim();
                    // Labeled format: 30w 24h 2d  /  24l 18w 6d  etc.
                    const wM = n.match(/(\d+(?:\.\d+)?)\s*w/);
                    const hM = n.match(/(\d+(?:\.\d+)?)\s*h/);
                    const dM = n.match(/(\d+(?:\.\d+)?)\s*d/);
                    const lM = n.match(/(\d+(?:\.\d+)?)\s*l/);
                    if (wM || hM || dM || lM) {
                        const w = wM ? wM[1] : (lM ? lM[1] : null);
                        const h = hM ? hM[1] : null;
                        const d = dM ? dM[1] : '2';
                        if (w && h) return `${w}x${d}x${h}`;
                        if (w)     return `${w}x${d}x${w}`;
                    }
                    // Plain numeric format: 24x18  /  30x24x2
                    const plainMatch = n.match(/^(\d+(?:\.\d+)?)[x\s](\d+(?:\.\d+)?)(?:[x\s](\d+(?:\.\d+)?))?/);
                    if (plainMatch) {
                        const p1 = plainMatch[1], p2 = plainMatch[2], p3 = plainMatch[3];
                        if (p3) return `${p1}x${p2}x${p3}`;
                        return `${p1}x2x${p2}`;
                    }
                    return null;
                };

                // ✅ LOGIC: Process each item individually into its own main box
                const newFieldsTest = [];

                // --- GROUP UNFRAMED PICTURES START ---
                const processedItems = [];
                const unframedPictures = [];

                items.forEach(singleItem => {
                    const itemName = (singleItem.item_name || "").toLowerCase();
                    const notes = (singleItem.notes || "").toLowerCase().trim();
                    const categoryName = (singleItem.category_name || singleItem.category || singleItem.item_category || "").toLowerCase();
                    
                    const isUnframed = notes.includes("unframed") || notes.includes("un-framed") || notes.includes("rolled") || itemName.includes("unframe") || categoryName.includes("unframe");

                    if (isUnframed) {
                        unframedPictures.push(singleItem);
                    } else {
                        processedItems.push(singleItem);
                    }
                });

                if (unframedPictures.length > 0) {
                    let totalDepth = 0;
                    let maxW = 0;
                    let maxL = 0;
                    let totalWeight = 0;

                    unframedPictures.forEach(item => {
                        const rawNotesSingle = (item.notes || "").trim();
                        const dimsFromNotes = extractDimsFromNotes(rawNotesSingle);
                        const effectiveDimsSingle = dimsFromNotes || item.dimensions;
                        let d = 2, l = 0, w = 0;
                        if (effectiveDimsSingle) {
                            const numbers = effectiveDimsSingle.match(/\d+(?:\.\d+)?/g);
                            if (numbers) {
                                if (numbers.length >= 3) {
                                    l = parseFloat(numbers[0]);
                                    d = parseFloat(numbers[1]);
                                    w = parseFloat(numbers[2]);
                                } else if (numbers.length === 2) {
                                    l = parseFloat(numbers[0]);
                                    w = parseFloat(numbers[1]);
                                }
                            }
                        }
                        totalDepth = Math.max(totalDepth, d);
                        maxL = Math.max(maxL, l);
                        maxW = Math.max(maxW, w);
                        totalWeight += parseFloat(item.weight || 0);
                    });

                    // Option A: Confirm test Box Size logic
                    let groupDepth = 6;
                    if (totalDepth <= 6) {
                        groupDepth = 6;
                    } else if (totalDepth <= 8) {
                        groupDepth = 8;
                    } else {
                        groupDepth = totalDepth;
                    }

                    const groupedItem = {
                        ...unframedPictures[0],
                        dimensions: `${maxL}x${groupDepth}x${maxW}`,
                        item_name: `Grouped Unframed Pictures (${unframedPictures.length} items)`,
                        notes: 'unframed',
                        weight: totalWeight > 0 ? totalWeight.toString() : '20'
                    };
                    processedItems.push(groupedItem);
                }
                // --- GROUP UNFRAMED PICTURES END ---

                processedItems.forEach((singleItem, idx) => {
                    const rawNotesSingle = (singleItem.notes || "").trim();
                    const itemName = (singleItem.item_name || "").toLowerCase();
                    const categoryName = (singleItem.category_name || singleItem.category || singleItem.item_category || "").toLowerCase();
                    const notes = rawNotesSingle.toLowerCase();
                    const isUnframed = notes.includes("unframe") || notes.includes("un-framed") || notes.includes("rolled") || itemName.includes("unframe") || categoryName.includes("unframe");
                    const isPictureOrFrame = itemName.includes("picture") || (!isUnframed && itemName.includes("frame")) || itemName.includes("works") || itemName.includes("painting") || categoryName.includes("picture") || (!isUnframed && categoryName.includes("frame"));
                    const isPicture = isPictureOrFrame || isUnframed;

                    const dimsFromNotes = extractDimsFromNotes(rawNotesSingle);
                    const effectiveDimsSingle = dimsFromNotes || singleItem.dimensions;
                    if (dimsFromNotes) console.log(` Using dims from notes: "${rawNotesSingle}" ➔ ${dimsFromNotes}`);
                    
                    // Get the packaging type based on notes
                    const isFragile = !isUnframed && (
                        notes.includes("glass") || notes.includes("acrylic") || notes.includes("etch") ||
                        (!isUnframed && notes.includes("framed")) || notes === ""
                    );
                    const isStandard = !isFragile;
                    
                    const paddedSize = calculatePaddedSize(effectiveDimsSingle, isFragile, isUnframed, isStandard);
                    const pictureBoxes = ["24x6x18", "24x18x6", "30x24x6", "30x6x24", "36x8x30", "36x6x42", "40x8x50", "54x8x28", "48x6x32", "42x8x60"];
                    const availableBoxes = isPicture 
                        ? BoxSize.value.filter(b => pictureBoxes.includes(b.value))
                        : BoxSize.value;

                    let boxSize = findClosestBoxSize(paddedSize, isPicture, true, false);
                    
                    // Prioritize tight-fit recommendation if it's the only item
                    if (processedItems.length === 1 && recommendedBoxSize) {
                        boxSize = recommendedBoxSize;
                    }
                    
                    const paddedDims = parseBoxSize(paddedSize);
                    let isTelescopedSub = false;
                    let telescopedBoxesSub = [];
                    let finalConfirmSizeSub = '';
                    let telescopedCombinedSub = '';

                    // Apply telescoping fallback if no single box fits
                    if (!boxSize) {
                        const needTelescoping = needsTelescoping(paddedDims, availableBoxes);
                        if (needTelescoping) {
                            const telescoped = findTelescopedBox(paddedDims, availableBoxes);
                            if (telescoped && telescoped.originalBox) {
                                boxSize = telescoped.originalBox;
                                isTelescopedSub = true;
                                telescopedBoxesSub = Array(telescoped.numBoxes || 2).fill(telescoped.originalBoxTitle || telescoped.originalBox);
                                telescopedCombinedSub = telescoped.value;
                                finalConfirmSizeSub = paddedSize;
                            }
                        }
                    }
                    
                    // If items are unframed, do not use a sub-box.
                    const useSubBox = isFragile && !isUnframed;

                    let finalType = isFragile ? 'Fragile' : 'Standard';
                    let finalBoxSize = '';
                    let finalConfirmSize = '';
                    let finalSubBox = null;
                    let isTelescoped = false;
                    let selectedSizes = [];

                    if (useSubBox) {
                        const matchedFragileBox = BoxSize.value.find(b => b.value === boxSize);
                        const displayFragileSize = isTelescopedSub ? finalConfirmSizeSub : (matchedFragileBox ? matchedFragileBox.title : (boxSize || '8x8x8'));
                        
                        const subBoxObj = {
                            type: 'Fragile',
                            size: boxSize || '8x8x8',
                            originalSize: effectiveDimsSingle,
                            paddedSize: paddedSize,
                            weight: singleItem.weight || '20',
                            itemName: singleItem.item_name || `Item ${idx + 1}`,
                            is_telescoped: isTelescopedSub,
                            telescoped_combined: telescopedCombinedSub,
                            telescoped_boxes: telescopedBoxesSub,
                            selected_sizes: isTelescopedSub ? telescopedBoxesSub : (matchedFragileBox ? [matchedFragileBox.title] : (boxSize ? [boxSize] : [])),
                            isPicture: isPicture
                        };

                        newFieldsTest.push({
                            type: finalType,
                            size: boxSize || '8x8x8',
                            confirm_size: displayFragileSize,
                            original_main_size: boxSize || '8x8x8',
                            original_confirm_size: displayFragileSize,
                            original_type: finalType,
                            weight: singleItem.weight || '20',
                            subBox: null,
                            subBoxes: [subBoxObj],
                            is_telescoped: isTelescopedSub,
                            selected_sizes: isTelescopedSub ? telescopedBoxesSub : (matchedFragileBox ? [matchedFragileBox.title] : (boxSize ? [boxSize] : [])),
                            isPicture: isPicture
                        });
                    } else {
                        // First, check if a single box fits
                        if (!isTelescopedSub && boxSize) {
                            finalBoxSize = boxSize;
                            finalConfirmSize = paddedSize;
                            const matchedFragileBox = BoxSize.value.find(b => b.value === boxSize);
                            selectedSizes = matchedFragileBox ? [matchedFragileBox.title] : [boxSize];
                        } else if (isTelescopedSub) {
                            finalBoxSize = boxSize;
                            finalConfirmSize = finalConfirmSizeSub;
                            isTelescoped = true;
                            selectedSizes = telescopedBoxesSub;
                            finalSubBox = { type: 'Fragile', size: boxSize, weight: 0 };
                        } else {
                            finalBoxSize = '';
                            finalConfirmSize = paddedSize;
                            isTelescoped = false;
                        }

                        newFieldsTest.push({
                            type: finalType,
                            size: finalBoxSize,
                            confirm_size: finalConfirmSize,
                            weight: singleItem.weight || '20',
                            subBox: finalSubBox,
                            subBoxes: null,
                            is_telescoped: isTelescoped,
                            selected_sizes: selectedSizes,
                            isPicture: isPicture
                        });
                    }
                });

                fields_test.value = newFieldsTest;
                fields_test.value.forEach(field => {
                    if (field.subBoxes && field.subBoxes.length > 0) {
                        updateMainBoxFromSubBoxes(field);
                    }
                });

            } else if (bestBox.value && bestBox.value.value) {
                fields_test.value = [{
                    type: 'Fragile',
                    size: bestBox.value.value,
                    confirm_size: bestBox.value.value,
                    weight: '20',
                    subBox: null,
                    subBoxes: null,
                    selected_sizes: [bestBox.value.value]
                }];
            } else {
                fields_test.value = [{
                    type: 'Fragile',
                    size: '',
                    confirm_size: '',
                    weight: '20',
                    subBox: null,
                    subBoxes: null,
                    selected_sizes: []
                }];
            }
        }
        
        // Auto-select boxes for telescoping on page reload if not already restored from saved state
        if (fields_test.value && fields_test.value.length > 0) {
            fields_test.value.forEach(field => {
                if (field.confirm_size && !field.is_telescoped) {
                    onConfirmSizeInput_test(field);
                }
            });
        }
        
        isGetQuote.value = false;
        
        // First time auto save for test mode without clicking the get price button
        // if (!hasAutoSyncedPrice && fields_test.value && fields_test.value.some(f => f.size || (Array.isArray(f.selected_sizes) && f.selected_sizes.length > 0))) {
        //     hasAutoSyncedPrice = true;
        //     setTimeout(() => {
        //         console.log("First time auto save without clicking Get Price button (Test Mode)...");
        //         submit_test();
        //     }, 350);
        // }

        if (response.data.shippingRates?.length > 0) {
            prizes.value = response.data.shippingRates.map((item) => {
                return {
                    label: item.Service,
                    value: item.Rate,
                    code: item.Code,
                    insurance: item.InsurancePrice,
                    signature: item.SignaturePrice,
                    baseServiceCharge: item.BaseServiceCharge,
                    allCharges: item.AllCharges || []
                };
            });

            // insurance sync
            if (parseFloat(insuranceCost.value) > 0 && selectedShippingCode.value) {
                const expectedInsurance = totalInsuranceAmount.value;
                if (parseFloat(insuranceCost.value) !== expectedInsurance) {
                    insuranceCost.value = expectedInsurance;

                    axios.post("api/selectInsuranceTest", {
                        id: decodeURIComponent(atob(route.params.id)),
                        price: expectedInsurance
                    }).catch((error) => { console.error("Error syncing insurance:", error); });
                }
            }

            // signature sync
            if (parseFloat(signatureCost.value) > 0 && selectedShippingCode.value) {
                const selectedPrize = prizes.value.find(p => parseInt(p.code) === parseInt(selectedShippingCode.value));

                const expectedSignature = totalSignatureAmount.value;
                if (selectedPrize && parseFloat(signatureCost.value) !== expectedSignature) {
                    signatureCost.value = expectedSignature;

                    axios.post("api/selectSignatureTest", {
                        id: decodeURIComponent(atob(route.params.id)),
                        price: expectedSignature
                    }).catch((error) => { console.error("Error syncing signature:", error); });
                }
            }
        }
    }).catch((error) => {
        isGetQuote.value = false;
        console.error(error);
        store.commit('setErrorNotification', "Failed to load quotation data. Please try again.");
        setTimeout(() => {
            store.commit('clearNotifications');
        }, 7000);
    });
}
const selectPrice = (price, code) => {
    isGetQuote.value = true;
    insuranceCost.value = 0;
    signatureCost.value = 0;
    axios.post("api/selectPriceTest", { id: decodeURIComponent(atob(route.params.id)), price: price, code: code }).then((res) => {
        shippingCost.value = price;
        selectedShippingCode.value = code;
        isGetQuote.value = false;
        console.log(res.data)
    })
}

const selectSignature = (price, signCost) => {
    console.log("Price:", price);
    console.log("Signature Cost:", signCost);
    const signaturePrice = signCost == 0 ? price : 0;
    signatureCost.value = signaturePrice;
    axios.post("api/selectSignatureTest", { id: decodeURIComponent(atob(route.params.id)), price: signaturePrice }).then((res) => {
        console.log(res.data);
    })
}
const selectInsurance = (price, insCost) => {
    console.log("Price:", price);
    console.log("Insurance Cost:", insCost);

    const insurancePrice = (Number(insCost) === 0 || !insCost) ? price : 0;

    console.log("Calculated Insurance Price:", insurancePrice);

    insuranceCost.value = insurancePrice;

    axios.post("api/selectInsuranceTest", {
        id: decodeURIComponent(atob(route.params.id)),
        price: insurancePrice
    })
        .then((res) => {
            console.log("Full Response:", res.data);
            console.log("Insurance Cost:", res.data.insuranceCost);
            console.log("Quote Data:", res.data.quote);
        })
        .catch((err) => {
            console.log("API Error:", err);
        });
}

const validatePickupCharge = (event) => {
    const value = Number((event.target).value)
    if (isNaN(value) || value < 0) {
        pickupCharge.value = 0
    }
}


//dynamically calculate signature cost based on selected shipping option
const signatureUnitPrice = computed(() => {
    if (signature_per_box.value > 0) {
        return Number(signature_per_box.value).toFixed(2);
    }
    const isUS = (qoutationData.value?.ship_to_country || 'US').trim().toUpperCase() === 'US';
    const unitRate = isUS ? signature_domestic.value : signature_international.value;
    return Number(unitRate || 0).toFixed(2);
});

const totalSignatureAmount = computed(() => {
    return parseFloat((parseFloat(signatureUnitPrice.value) * masterBoxesCount.value).toFixed(2));
});

// dynamically calculate insurance rate per $100 and total insurance amount
const insuranceRatePer100 = computed(() => {
    if (parseFloat(insurance_per_box.value) > 0) {
        return Number(insurance_per_box.value).toFixed(2);
    }
    const isUS = (qoutationData.value?.ship_to_country || 'US').trim().toUpperCase() === 'US';
    const itemVal = parseFloat(qoutationData.value?.total || 0) || (qoutationItems.value && qoutationItems.value.length > 0 ? qoutationItems.value.reduce((acc, it) => acc + (parseFloat(it.lot_price) || 0), 0) : 0);
    const boxCount = masterBoxesCount.value > 0 ? masterBoxesCount.value : 1;
    const valuePerBox = itemVal / boxCount;

    // Evaluate matching tier rate from database tiers:
    if (valuePerBox > 10000) return Number(2.50).toFixed(2);
    if (valuePerBox > 2000) return Number(isUS ? 2.75 : 2.71).toFixed(2);
    if (valuePerBox > 1000) return Number(3.00).toFixed(2);
    if (valuePerBox > 500) return Number(3.25).toFixed(2);

    const unitRate = isUS ? insurance_domestic.value : insurance_international.value;
    return Number(unitRate || 3.50).toFixed(2);
});

const totalInsuranceAmount = computed(() => {
    // 1. If currently selected shipping prize has insurance > 0, use it
    const selected = prizes.value.find(p => parseInt(p.code) === parseInt(selectedShippingCode.value));
    if (selected && parseFloat(selected.insurance) > 0) {
        return parseFloat(parseFloat(selected.insurance).toFixed(2));
    }
    
    // 2. If any prize in prizes has insurance > 0
    const anyPrize = prizes.value.find(p => parseFloat(p.insurance) > 0);
    if (anyPrize && parseFloat(anyPrize.insurance) > 0) {
        return parseFloat(parseFloat(anyPrize.insurance).toFixed(2));
    }

    // 3. If backend returned insurance_price in response.data:
    if (parseFloat(insurance_price.value) > 0) {
        return parseFloat(parseFloat(insurance_price.value).toFixed(2));
    }

    // 4. Dynamic calculation:
    // First $100 is free per outer box: (total/boxCount - 100)
    const itemVal = parseFloat(qoutationData.value?.total || 0) || (qoutationItems.value && qoutationItems.value.length > 0 ? qoutationItems.value.reduce((acc, it) => acc + (parseFloat(it.lot_price) || 0), 0) : 0);
    const boxCount = masterBoxesCount.value > 0 ? masterBoxesCount.value : 1;
    const valuePerBox = itemVal / boxCount;

    if (valuePerBox > 100) {
        const coverAmtPerBox = valuePerBox - 100;
        const unitsPerBox = Math.ceil(coverAmtPerBox / 100);
        const rate = parseFloat(insuranceRatePer100.value) || 3.50;
        return parseFloat((unitsPerBox * rate * boxCount).toFixed(2));
    }
    return 0;
});

const submitMail = () => {
    // if (confirm("Are you sure you want to send the email?")) {
    isMail.value = true;
    if (shippingCost.value <= 0) {
        showProceedModel.value = false;
        store.commit('setErrorNotification', "Please select a shipping option before sending the email.");
        store.commit('setSuccessNotification', null);
        setTimeout(() => {
            store.commit('clearNotifications');
        }, 3000);
        isMail.value = false;
        return;
    }
    axios.post("api/sendQuotationMailTest", { id: decodeURIComponent(atob(route.params.id)), shippingRates: prizes.value, pickupCharge: pickupCharge.value == '' ? 0 : pickupCharge.value }).then((res) => {
        console.log(res.data)
        showProceedModel.value = false;
        store.commit('setSuccessNotification', "Email sent successfully");
        store.commit('setErrorNotification', null);
        setTimeout(() => {
            store.commit('clearNotifications');
        }, 3000);
        isMail.value = false;
    }).catch((error) => {
        isMail.value = false;
        showProceedModel.value = false;
        if (error.response?.data && error.response?.data.error) {
            store.commit('setErrorNotification', error.response.data.error);
            store.commit('setSuccessNotification', null);
        }
        setTimeout(() => {
            store.commit('clearNotifications');
        }, 3000);
        console.error(error);
    });
    // }
};

onMounted(() => {
    getSingleQuote();
    checkAIStatus();
    statusInterval = setInterval(checkAIStatus, 60000);
    watch(boxesCount, async (newVal) => {
        if (newVal.length === 0 && !isGetQuote.value && !showModal.value) {
            await nextTick();
            showModal.value = true;
        }
    }, { immediate: true });
});

onBeforeUnmount(() => {
    if (statusInterval) {
        clearInterval(statusInterval);
    }
});
</script>

<template>
    <div v-if="packingResult && packingResult.type === 'single' && packingResult.box && packingResult.item"
        class="mt-3">
        <h4> Packing Details</h4>
        <p><strong>Box:</strong> {{ packingResult.box.value || packingResult.box.title }}</p>
        <p v-if="packingResult.box.dims">
            <strong>Box Dimensions:</strong> {{ packingResult.box.dims.l }} x {{ packingResult.box.dims.w }} x {{
                packingResult.box.dims.h }}
        </p>
        <p>
            <strong>Item Size:</strong>
            {{ packingResult.item.l }} x {{ packingResult.item.w }} x {{ packingResult.item.h }}
        </p>
    </div>

    <div v-if="packingResult && packingResult.type === 'combined' && packingResult.boxes && packingResult.boxes.length && packingResult.item"
        class="mt-3">
        <h4> Combined Packing</h4>
        <div v-for="(box, i) in packingResult.boxes" :key="i">
            <p><strong>Box {{ i + 1 }}:</strong> {{ box.name }}</p>
            <p v-if="box.dims">Dimensions: {{ box.dims.l }} x {{ box.dims.w }} x {{ box.dims.h }}</p>
        </div>
        <p>
            <strong>Total Item Size:</strong>
            {{ packingResult.item.l }} x {{ packingResult.item.w }} x {{ packingResult.item.h }}
        </p>
    </div>
    <div class="py-4 container-fluid ctm-mobile-padding">
        <div class="row mt-4">
            <!-- <button class="upload-btn" @click="openModal">Add New Quotation </button>  -->
            <div class="col-lg-12 mb-lg-0 mb-4">
                <div class="card p-3 p-md-5 shadow-lg rounded-xl bg-light-color text-gray-800 position-relative">
                    <div class="ctm-loader" v-if="isMail || isPrice || isGetQuote">
                        <div class="loader" id="loader-5">
                            <span></span>
                            <span></span>
                            <span></span>
                            <span></span>
                        </div>
                    </div>
                    <!-- Header -->
                    <div class="d-flex justify-space-between align-items-center mb-2">
                        <h2 class="text-2xl font-bold box-heading">Test Quotation</h2>
                    </div>
                    <div class="d-flex justify-content-between align-items-center pb-2 mb-3">
                        <div>
                            <p class="text-sm text-gray-500 mb-1"><strong>Quotation No:</strong>
                                {{ qoutationData.quotation_number }}</p>
                            <p class="text-sm text-gray-500 mb-1"><strong>Date:</strong> {{ qoutationData.shipment_date
                                }}</p>
                            <p class="text-sm text-gray-500 mb-1"><strong>Customer Name:</strong> {{
                                qoutationData.customer_name }}</p>
                            <p class="text-sm text-gray-500 mb-1"><strong>Ship To:</strong>
                                {{ qoutationData.ship_to_address }},{{ qoutationData.ship_to_cityzip }} {{
                                    qoutationData.ship_to_country }}</p>
                            <p class="text-sm text-gray-500 mb-1"><strong>Phone No:</strong> {{
                                qoutationData.customer_phone }}</p>
                            <p class="text-sm text-gray-500 mb-1"><strong>Email:</strong> {{
                                qoutationData.customer_email
                                }}</p>
                        </div>
                    </div>

                    <div class="row">
                        <div class="col-md-12">
                            <!-- Modal -->

                        </div>
                    </div>
                </div>
                <!-- Auction Summary -->
                <!-- Auction Summary -->
                <div class="auction-content d-flex justify-content-between align-items-center mb-4">
                    <button @click="showModal = !showModal" class="open-btn" :disabled="showPrizeCard"
                        :class="showPrizeCard ? 'bg-light-color' : ''" :style="showPrizeCard ? 'cursor: default;' : ''">
                        {{ showModal ? (!isTestModeActive ? 'Add Boxes' : 'TEST Add Boxes') : (!isTestModeActive ? 'Add Boxes' : 'TEST Add Boxes') }}
                    </button>
                </div>
                <!-- Table -->
                <div class="overflow-x-auto table-overflow">
                    <table class="border border-gray-300 w-100">
                        <thead class="bg-gray-100">
                            <tr>
                                <th class="p-3 text-left">Lot No</th>
                                <th class="p-3 text-left">Item Name</th>
                                <th class="p-3 text-left">Size</th>
                                <th class="p-3 text-left">Quantity</th>
                                <th class="p-3 text-left">Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            <template v-for="(item, index) in qoutationItems" :key="index">
                                <tr v-if="!item.is_duplicate_display" class="border-t border-gray-200">
                                    <td class="p-3">{{ item.lot_number }}</td>
                                    <td class="p-3">{{ item.item_name }}</td>
                                    <td class="p-3">
                                        {{ item.dimensions }}
                                    </td>
                                    <td class="p-3">{{ item.quantity }}</td>
                                    <td class="p-3">{{ item.lot_price !== '' && item.lot_price !== null ? '$' + item.lot_price : '' }}</td>
                                </tr>
                            </template>
                            <tr class="bg-gray-50 font-semibold">
                                <td colspan="3"></td>
                                <td class="p-3"><strong>Total</strong></td>
                                <td class="p-3"><strong>${{ qoutationData.total }}</strong></td>
                                <td colspan="3"></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
            <div class="row">
                <div class="col-md-12">
                    <!-- Single Conditional Modal - Shows based on whether boxes exist -->
                    <div v-if="showModal" class="card h-100 p-4 rounded-xl mt-4 bg-dark-yellow bg-gray-50">
                        <!-- Dynamic Header -->
                        <h3 class="text-xl font-semibold text-[#7b5540] mb-4 box-heading">
                            {{ !isTestModeActive ? 'Add Boxes' : 'TEST Add Boxes' }}
                        </h3>

                        <!-- Show fields_test when test mode layout is active -->
                        <div v-if="isTestModeActive">
                            <div v-for="(field_test, fieldIdx) in fields_test" :key="fieldIdx"
                                class="border border-white p-4 rounded mb-3">
                                <!-- Box Type -->
                                <div class="row w-100">
                                    <div class="col-md-3">
                                        <label>Packaging Test Type</label>
                                        <select v-model="field_test.type" class="input">
                                            <option disabled value="">Select Packaging Type</option>
                                            <option>Standard</option>
                                            <option>Fragile</option>
                                            <option>Custom</option>
                                        </select>
                                    </div>
                                    <div class="col-md-3">
                                        <div class="d-flex align-items-center mb-1">
                                            <label class="mb-0">Box test Size</label>
                                            <div v-if="field_test.selected_sizes && field_test.selected_sizes.length > 0" class="d-flex align-items-center bg-white rounded ms-2" style="border: 1px solid #d7c5bc; padding: 1px 6px; gap: 8px;">
                                                <span class="cursor-pointer" @click.stop="decrementBox(field_test)" :style="field_test.selected_sizes.length <= 1 ? 'opacity: 0.4; cursor: not-allowed;' : ''" style="color: #7d5341; font-size: 0.9rem; line-height: 1;">-</span>
                                                <span class="font-weight-bold" style="color: #7d5341; font-size: 0.8rem;">{{ field_test.selected_sizes.length }}</span>
                                                <span class="cursor-pointer" @click.stop="incrementBox(field_test)" :style="field_test.selected_sizes.length >= 3 ? 'opacity: 0.4; cursor: not-allowed;' : ''" style="color: #7d5341; font-size: 0.9rem; line-height: 1;">+</span>
                                            </div>
                                        </div>
                                        <!-- Show Simple Select Dropdown -->
                                        <select class="input"
                                                :value="field_test.selected_sizes && field_test.selected_sizes.length > 0 ? field_test.selected_sizes[0] : ''"
                                                @change="field_test.selected_sizes = Array(field_test.selected_sizes && field_test.selected_sizes.length > 0 ? field_test.selected_sizes.length : 1).fill($event.target.value)"
                                                style="height: 42px; border: 1px solid #d2d6da; border-radius: 0.5rem; color: #344767; background-color: #fff; cursor: pointer;">
                                            <option value="" disabled selected>Select Box Size...</option>
                                            <option v-for="(box, bIdx) in filteredBoxSizesForField(field_test)" :key="'dd_' + bIdx" :value="box.title">
                                                {{ box.title }}
                                            </option>
                                        </select>
                                    </div>
                                    <div class="col-md-3">
                                        <label>Confirm test Box Size</label>
                                        <input type="text" v-model="field_test.confirm_size"
                                            @input="onConfirmSizeInput_test(field_test)" placeholder="e.g. 9x6x3"
                                            maxlength="20" class="input" />
                                    </div>
                                    <div class="col-md-3">
                                        <label>Enter weight (LBS):</label>
                                        <input type="text" min="0" v-model="field_test.weight" placeholder="e.g., 65"
                                            oninput="this.value = this.value.replace(/[^0-9.]/g, '')" class="input" />
                                    </div>
                                    <div class="col-md-3 d-flex align-items-end mt-2 mt-md-0">
                                        <div class="d-flex align-items-end gap-2">
                                            <button v-if="fields_test.length > 1" @click="removeField_test(fieldIdx)"
                                                class="remove-input ml-2">X</button>
                                            <div v-if="!field_test.subBoxes || field_test.subBoxes.length === 0">
                                                <button @click="addSubBox_test(fieldIdx)"
                                                    class="btn btn-secondary mb-0">Add Test Sub-Boxes</button>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <!-- Sub Boxes Section - Display all sub-boxes -->
                                <div v-if="field_test.subBoxes && field_test.subBoxes.length > 0">
                                    <div v-for="(subBox, subIdx) in field_test.subBoxes" :key="subIdx" class="row mt-2 mb-3 p-4 border px-2 bg-white mx-1">
                                        <div class="col-md-5">
                                            <label>Packaging Type Test</label>
                                            <select v-model="subBox.type" class="input" @change="updateMainBoxFromSubBoxes(field_test)">
                                                <option disabled value="">Select Packaging Type</option>
                                                <option>Standard</option>
                                                <option>Fragile</option>
                                                <option>Custom</option>
                                            </select>
                                        </div>
                                        <div class="col-md-5">
                                            <div class="d-flex align-items-center mb-1">
                                                <label class="mb-0">Sub Box Size Test</label>
                                                <div v-if="subBox.selected_sizes && subBox.selected_sizes.length > 0" class="d-flex align-items-center bg-white rounded ms-2" style="border: 1px solid #d7c5bc; padding: 1px 6px; gap: 8px;">
                                                    <span class="cursor-pointer" @click.stop="decrementBox(subBox); subBox.isManualChoice = true; updateMainBoxFromSubBoxes(field_test)" :style="subBox.selected_sizes.length <= 1 ? 'opacity: 0.4; cursor: not-allowed;' : ''" style="color: #7d5341; font-size: 0.9rem; line-height: 1;">-</span>
                                                    <span class="font-weight-bold" style="color: #7d5341; font-size: 0.8rem;">{{ subBox.selected_sizes.length }}</span>
                                                    <span class="cursor-pointer" @click.stop="incrementBox(subBox); subBox.isManualChoice = true; updateMainBoxFromSubBoxes(field_test)" :style="subBox.selected_sizes.length >= 3 ? 'opacity: 0.4; cursor: not-allowed;' : ''" style="color: #7d5341; font-size: 0.9rem; line-height: 1;">+</span>
                                                </div>
                                            </div>
                                            <!-- Simple dropdown for Sub Box -->
                                            <select class="input"
                                                    :value="subBox.selected_sizes && subBox.selected_sizes.length > 0 ? subBox.selected_sizes[0] : ''"
                                                    @change="subBox.selected_sizes = Array(subBox.selected_sizes && subBox.selected_sizes.length > 0 ? subBox.selected_sizes.length : 1).fill($event.target.value); subBox.isManualChoice = true; updateMainBoxFromSubBoxes(field_test)"
                                                    style="height: 42px; border: 1px solid #d2d6da; border-radius: 0.5rem; color: #344767; background-color: #fff; cursor: pointer;">
                                                <option value="" disabled selected>Select Sub-Box Size...</option>
                                                <option v-for="(box, bIdx) in filteredBoxSizesForField(subBox)" :key="'sdd_' + bIdx" :value="box.title">
                                                    {{ box.title }}
                                                </option>
                                            </select>
                                        </div>
                                        <div class="col d-flex align-items-end">
                                            <button @click="removeSubBox_test(fieldIdx, subIdx)" class="remove-input mt-2">X</button>
                                        </div>
                                    </div>
                                </div>
                               
                            </div>
                            
                         

                            <div class="plus-group mt-4">
                                <button @click="addField_test" class="add-btn">Add Tested More</button>
                            </div>
                        </div>

                        <!-- Show fields when boxes exist from API -->
                        <div v-else>
                            <div v-for="(field, index) in fields" :key="index"
                                class="border border-white p-4 rounded mb-3">
                                <div class="row w-100">
                                    <div class="col-md-3">
                                        <label for="weight">Packaging Type</label>
                                        <select v-model="field.type" class="input">
                                            <option disabled value="">Select Packaging Type</option>
                                            <option>Standard</option>
                                            <option>Fragile</option>
                                            <option>Custom</option>
                                        </select>
                                    </div>
                                    <div class="col-md-3">
                                        <div class="d-flex align-items-center mb-1">
                                            <label for="weight" class="mb-0">Box Size</label>
                                            <div v-if="field.selected_sizes && field.selected_sizes.length > 0" class="d-flex align-items-center bg-white rounded ms-2" style="border: 1px solid #d7c5bc; padding: 1px 6px; gap: 8px;">
                                                <span class="cursor-pointer" @click.stop="decrementBox(field)" :style="field.selected_sizes.length <= 1 ? 'opacity: 0.4; cursor: not-allowed;' : ''" style="color: #7d5341; font-size: 0.9rem; line-height: 1;">-</span>
                                                <span class="font-weight-bold" style="color: #7d5341; font-size: 0.8rem;">{{ field.selected_sizes.length }}</span>
                                                <span class="cursor-pointer" @click.stop="incrementBox(field)" :style="field.selected_sizes.length >= 3 ? 'opacity: 0.4; cursor: not-allowed;' : ''" style="color: #7d5341; font-size: 0.9rem; line-height: 1;">+</span>
                                            </div>
                                        </div>
                                        <!-- Always show custom-multiselect -->
                                        <div class="custom-multiselect position-relative">
                                            <div v-if="field.showDropdown" 
                                                 @click="field.showDropdown = false" 
                                                 style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; z-index: 1040;">
                                            </div>

                                            <div class="form-control d-flex flex-wrap align-items-center" 
                                                 style="min-height: 42px; cursor: pointer; gap: 6px; padding: 6px 30px 6px 8px; background-color: #fff; position: relative; z-index: 1045; border: 1px solid #d2d6da; border-radius: 0.5rem; box-shadow: 0 2px 4px rgba(0,0,0,0.02); transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;"
                                                 @click="field.showDropdown = !field.showDropdown"
                                                 :style="field.showDropdown ? 'border-color: #7d5341; box-shadow: 0 0 0 2px rgba(125, 83, 65, 0.2);' : ''">
                                                
                                                <span v-if="!field.selected_sizes || field.selected_sizes.length === 0" 
                                                      class="text-muted small ms-1" style="font-size: 0.85rem;">
                                                    Select Box Sizes...
                                                </span>
                                                
                                                <span v-if="field.selected_sizes && field.selected_sizes.length > 0" 
                                                      class="badge d-flex align-items-center w-100" 
                                                      style="font-size: 0.75rem; font-weight: 600; padding: 6px 10px; background-color: #f3ece8; color: #7d5341; border: 1px solid #d7c5bc; border-radius: 6px; z-index: 1046; letter-spacing: 0.3px; gap: 8px;">
                                                    <span style="white-space: normal; text-align: left; line-height: 1.2; flex-grow: 1;">
                                                        {{ field.selected_sizes[0] }}
                                                    </span>
                                                    
                                                    <!-- Quantity Controls moved to label -->

                                                    <!-- Remove Button -->
                                                    <span class="font-weight-bold flex-shrink-0" 
                                                          style="cursor: pointer; font-size: 1rem; line-height: 1; padding-left: 4px; border-left: 1px solid #d7c5bc;" 
                                                          @click.stop="clearBoxSelection(field)">
                                                        &times;
                                                    </span>
                                                </span>
                                                
                                                <div class="text-muted" style="position: absolute; right: 10px; top: 50%; transform: translateY(-50%); font-size: 0.7rem; pointer-events: none;">
                                                    <i class="fas fa-chevron-down" :style="field.showDropdown ? 'transform: rotate(180deg); transition: transform 0.2s;' : 'transition: transform 0.2s;'"></i>
                                                </div>
                                            </div>
                                            
                                            <div v-if="field.showDropdown" 
                                                 class="dropdown-menu show w-100 p-2 border" 
                                                 style="position: absolute; top: calc(100% + 4px); left: 0; z-index: 1050; max-height: 250px; overflow-y: auto; background-color: #fff; border-radius: 0.5rem; box-shadow: 0 8px 26px -4px rgba(0,0,0,0.15), 0 8px 9px -5px rgba(0,0,0,0.06); border-color: rgba(0,0,0,0.05);">
                                                 
                                                <div v-for="(box, bIdx) in BoxSize.slice(1)" :key="'dd_' + bIdx"
                                                    class="dropdown-item px-3 py-2 rounded mb-1 d-flex align-items-center"
                                                    :style="{
                                                        backgroundColor: (Array.isArray(field.selected_sizes) && field.selected_sizes.includes(box.title)) ? '#f3ece8' : 'transparent',
                                                        cursor: (Array.isArray(field.selected_sizes) && field.selected_sizes.length > 0 && field.selected_sizes[0] !== box.title) ? 'not-allowed' : 'pointer',
                                                        opacity: (Array.isArray(field.selected_sizes) && field.selected_sizes.length > 0 && field.selected_sizes[0] !== box.title) ? 0.4 : 1,
                                                        color: '#344767',
                                                        fontWeight: (Array.isArray(field.selected_sizes) && field.selected_sizes.includes(box.title)) ? '600' : '500',
                                                        transition: 'background-color 0.2s ease'
                                                    }"
                                                    @click="(!Array.isArray(field.selected_sizes) || field.selected_sizes.length === 0 || field.selected_sizes[0] === box.title) && toggleBoxSelection(field, box.title)">
                                                    
                                                    <div class="d-flex align-items-center justify-content-center me-3 flex-shrink-0" 
                                                         style="width: 20px; height: 20px; border-radius: 5px; border: 2px solid; transition: all 0.2s ease;"
                                                         :style="(Array.isArray(field.selected_sizes) && field.selected_sizes.includes(box.title)) ? 'background-color: #7d5341; border-color: #7d5341;' : 'background-color: #fff; border-color: #ced4da;'">
                                                        <i v-if="Array.isArray(field.selected_sizes) && field.selected_sizes.includes(box.title)" 
                                                           class="fas fa-check" style="color: white; font-size: 11px;"></i>
                                                    </div>
                                                    
                                                    <span style="font-size: 0.85rem; letter-spacing: 0.2px;">{{ box.title }}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="col-md-3">
                                        <label for="weight">Confirm Box Size</label>
                                        <input id="boxSizeInput" type="text" v-model="field.confirm_size"
                                            @input="onConfirmSizeInput(field)" placeholder="e.g. 9x6x3" maxlength="20"
                                            class="input" />
                                    </div>
                                    <div class="col-md-3">
                                        <label for="weight">Enter weight (LBS):</label>
                                        <input type="text" id="weight" min="0" name="weight" v-model="field.weight"
                                            placeholder="e.g., 65"
                                            oninput="this.value = this.value.replace(/[^0-9.]/g, '')" class="input" />
                                    </div>
                                    <div class="col-md-3 d-flex align-items-end">
                                        <div class="d-flex align-items-end gap-2">
                                            <button v-if="fields.length > 1" @click="removeField(index)"
                                                class="remove-input ml-2">X</button>
                                            <div v-if="!field.subBox" class="">
                                                <button @click="addSubBox(index)" class="btn btn-secondary mb-0">Add
                                                    Sub-Box</button>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div v-if="field.subBox" class="row mt-2 mb-3 p-4 border px-2 bg-white mx-1">
                                    <div class="col-md-5">
                                        <label for="weight">Packaging Type</label>
                                        <select v-model="field.subBox.type" class="input">
                                            <option disabled value="">Select Packaging Type</option>
                                            <option>Standard</option>
                                            <option>Fragile</option>
                                            <option>Custom</option>
                                        </select>
                                    </div>
                                    <div class="col-md-5">
                                        <label>Sub Box Size</label>
                                        <select v-model="field.subBox.size" class="input" :disabled="!field.size" @change="updateStandardMainBox(field)">
                                            <option v-if="field.subBox.size && !BoxSize.some(b => b.value && b.value.toLowerCase() === field.subBox.size.toLowerCase())" :value="field.subBox.size">
                                                {{ field.subBox.size }}
                                            </option>
                                            <option v-for="(boxes, index) in BoxSize" :key="index" :value="boxes.value"
                                                :disabled="boxes.value === ''">{{ boxes.title }}</option>
                                        </select>
                                    </div>
                                    <div class="col d-flex align-items-end">
                                        <button @click="removeSubBox(index)" class="remove-input mt-2">X</button>
                                    </div>
                                </div>
                            </div>
                            <div class="plus-group mt-4">
                                <button @click="addField" class="add-btn">Add More</button>
                            </div>
                        </div>

                        <!-- Common Pickup Charges and Actions -->
                        <div class="row mt-3">
                            <div class="col-6">
                                <div class="chargeDesign">
                                    <h5>PickUp Charges</h5>
                                    <input min="0" step="0.01" type="number" placeholder="Enter Pickup charge"
                                        v-model="pickupCharge" @input="validatePickupCharge">
                                </div>
                            </div>
                            <div class="col-6">
                                <div class="modal-actions">
                                    <button @click="isTestModeActive ? submit_test() : submit()"
                                        :disabled="isPrice">
                                        {{ isPrice ? "Getting Test Price..." : "Get Test Price" }}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="row mt-4 mb-4">
                    <div class="col-md-12">
                        <!-- New Prize Card (visible when button clicked) -->
                        <div class="row">
                            <div class="col-md-6 mb-3">
                                <div v-if="showPrizeCard && hasClickedTestPrice"
                                    class="card h-100 p-4 shadow-lg rounded-xl bg-yellow border border-yellow-300 mt-4">
                                    <h3 class="font-semibold text-yellow-800 mb-3 box-heading">UPS Shipping Options
                                        Price</h3>
                                    <div v-for="(prize, index) in prizes" :key="index"
                                        class="flex items-center gap-3 mb-2">

                                        <div v-if="prize.value === '0'">
                                            <input type="radio" :id="'prize' + index" :value="prize.value"
                                                class="cursor-pointer" :disabled="true"
                                                :checked="shippingCost === prize.value && prize.value !== '0'" />
                                            <label :for="'prize' + index" class="text-gray-700">{{ prize.label }} - ${{
                                                prize.value
                                                }}</label>
                                        </div>
                                        <div v-else>
                                            <input type="radio" :id="'prize' + index" :value="prize.value"
                                                :checked="parseFloat(shippingCost) === parseFloat(prize.value) && parseInt(prize.code) == parseInt(selectedShippingCode)"
                                                class="cursor-pointer" @click="selectPrice(prize.value, prize.code)" />
                                            <label :for="'prize' + index" class="text-gray-700">{{ prize.label }} - ${{
                                                prize.value
                                                }}</label>
                                        </div>
                                        <div v-if="parseFloat(shippingCost) === parseFloat(prize.value) && parseInt(prize.code) == parseInt(selectedShippingCode)"
                                            class="cardOuter">
                                            <div class="card cardBackDesign shadow-none">
                                                <label :for="'prize' + index" class="text-gray-700">Charges
                                                    Include</label>
                                                <div class="card-body p-3">
                                                    <label :for="'prize' + index" class="text-gray-700">Base Service
                                                        Charge - ${{ prize.baseServiceCharge }}</label>
                                                    <div v-for="(charge, index) in prize.allCharges" :key="index"
                                                        class="text-gray-600 text-sm">
                                                        <div>
                                                            <span class="font-medium">{{ charge.description }}:</span>
                                                            <span v-if="['100'].includes(charge.code)">${{
                                                                parseFloat(charge.adjusted_amount ?? 0).toFixed(2)
                                                                }}</span>
                                                            <span
                                                                v-else-if="['270', '190', '376'].includes(charge.code)">${{
                                                                    parseFloat(charge.adjusted_amount ?? 0).toFixed(2)
                                                                }}</span>
                                                            <span v-else>${{ parseFloat(charge.amount).toFixed(2)
                                                                }}</span>
                                                        </div>

                                                        <template v-if="['100'].includes(charge.code)">
                                                            <div class="ml-4 text-xs text-primary italic">
                                                                → Margin for Additional Handling: ${{ charge.amount }} /
                                                                0.70
                                                            </div>
                                                        </template>

                                                        <template
                                                            v-else-if="['270', '190', '376'].includes(charge.code)">
                                                            <div class="ml-4 text-xs text-primary italic">
                                                                → Margin for Res Surcharge: ${{ charge.amount }} / 0.55
                                                            </div>
                                                        </template>

                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div v-if="parseFloat(shippingCost) === parseFloat(prize.value) && parseInt(prize.code) == parseInt(selectedShippingCode)"
                                            class="cardOuter">
                                            <div class="card cardBackDesign shadow-none">
                                                <label :for="'prize' + index" class="text-gray-700">Charges Exclude</label>
                                                <div class="card-body p-3">
                                                    <div>
                                                        <input type="radio" :id="'insurance' + index"
                                                            :value="totalInsuranceAmount" v-model="insuranceCost"
                                                            class="cursor-pointer"
                                                            @click="selectInsurance(totalInsuranceAmount, insuranceCost)" />
                                                        <label :for="'insurance' + index"
                                                            class="text-gray-700">Insurance - ${{
                                                                totalInsuranceAmount.toFixed(2)
                                                            }}</label>
                                                        <div v-if="parseFloat(insuranceRatePer100) > 0"
                                                            class="ml-4 text-xs text-primary italic">
                                                            → Based on ${{ insuranceRatePer100 }} per
                                                            $100
                                                            (excluding first $100 of lot value)
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <input type="radio" :id="'signature' + index"
                                                            :value="totalSignatureAmount" v-model="signatureCost"
                                                            class="cursor-pointer"
                                                            @click="selectSignature(totalSignatureAmount, signatureCost)" />
                                                        <label :for="'signature' + index"
                                                            class="text-gray-700">Signature - ${{
                                                                totalSignatureAmount.toFixed(2)
                                                            }}</label>
                                                        <div v-if="parseFloat(signatureUnitPrice) > 0"
                                                            class="ml-4 text-xs text-primary italic">
                                                            → ${{ signatureUnitPrice }} per outer box → {{ masterBoxesCount
                                                            }} outer box<span v-if="masterBoxesCount > 1">es</span> = ${{ totalSignatureAmount.toFixed(2) }}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <!-- <div class="my-2 text-green-800 font-medium" v-if="selectedPrize">
                                You selected: <strong>{{ selectedPrize }}</strong>
                            </div> -->
                                    <!-- <div class="send-mail mt-2">
                                <button @click="showPrizeCard = true">Send Email</button>
                            </div> -->
                                </div>
                            </div>
                            <div class="col-md-6 mb-3">
                                <div v-if="showPrizeCard && hasClickedTestPrice"
                                    class="card h-100 p-4 shadow-lg rounded-xl bg-yellow border border-yellow-300 mt-4">
                                    <h3 class="font-semibold text-yellow-800 mb-3 box-heading">Shipping Summary</h3>
                                    <table style="background-color: transparent;" class="shipping-table">
                                        <tr>
                                            <td>Shipping Cost</td>
                                            <td style="text-align:right;"><strong>${{
                                                parseFloat(shippingCost).toFixed(2) }}</strong></td>
                                        </tr>
                                        <tr>
                                            <td>Signature Cost</td>
                                            <td style="text-align:right;"><strong>${{
                                                parseFloat(signatureCost).toFixed(2) }}</strong></td>
                                        </tr>
                                        <tr>
                                            <td>Insurance Cost</td>
                                            <td style="text-align:right;"><strong>${{
                                                parseFloat(insuranceCost). toFixed(2) }}</strong>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>Packaging Cost</td>
                                            <td style="text-align:right;"><strong>${{
                                                parseFloat(packagingCost).toFixed(2) }}</strong></td>
                                        </tr>
                                        <tr>
                                            <td>Box Cost</td>
                                            <td style="text-align:right;"><strong>${{ parseFloat(boxCost).toFixed(2)
                                            }}</strong></td>
                                        </tr>
                                        <tr>
                                            <td>PickUp Charges</td>
                                            <td style="text-align:right;">
                                                <strong>${{ parseFloat(pickupCharge || 0).toFixed(2)
                                                }}</strong>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>CMS Processing Fee</td>
                                            <td style="text-align:right;">
                                                <strong>${{ parseFloat(cms_processing_fee).toFixed(2) }}</strong>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td class="total"><strong>Total Price</strong></td>
                                            <td class="total" style="text-align:right;"><strong>${{
                                                parseFloat(totalCost).toFixed(2)
                                                    }}</strong>
                                            </td>
                                        </tr>
                                    </table>


                                    <div class="send-mail mt-2">
                                        <button @click="showProceedModel = true" :disabled="isMail">{{ isMail ? "Confirm and Proceed..." : "Confirm and Proceed"}}</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>


    <div v-if="showProceedModel" class="modal-overlay">
        <div class="modal-container">
            <div class="modal-header">
                <h5 class="modal-title">Confirmation</h5>
                <button class="close-button" @click="showProceedModel = false">&times;</button>
            </div>
            <div class="modal-body">
                <p class="mb-0">are you sure you want to send a quotation mail to customer ?</p>
            </div>
            <div class="modal-footer">
                <button class="btn btn-bg-yellow" @click="showProceedModel = false">No</button>
                <button class="btn btn-bg" @click="submitMail">Yes</button>
            </div>
        </div>
    </div>



</template>


<style scoped>
.cardOuter .card.cardBackDesign {
    background: #ffffffba !important;
    margin-bottom: 20px !important;
}

.card.cardBackDesign label {
    font-size: 14px !important;
}

.card {
    margin: auto;
    box-shadow: 0 8px 26px -4px rgba(0, 0, 0, 0.08);
    background-color: #F2E5D7 !important;
    position: relative;
}

table {
    border-collapse: separate;
    border-radius: 10px;
    background-color: #fff;
}

.field-group {
    display: flex;
    gap: 0.5rem;
    margin-bottom: 1rem;
}

.input {
    flex: 1;
    padding: 0.5rem;
    border: 1px solid #ccc;
    border-radius: 5px;
}

.input:focus,
input:focus {
    outline: none;
}

.open-btn,
.modal-actions button {
    background: #a35c2e !important;
    border: none;
    color: #fff;
    border-radius: 5px;
    padding: 8px 30px;
    cursor: pointer;
    font-weight: 600;
    font-size: 14px;
}

.modal-actions {
    display: flex;
    margin-top: 20px;
    gap: 10px;
    justify-content: end;
}

.plus-group {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
}

.add-btn,
.remove-input {
    background: #a35c2e !important;
    color: #fff;
    padding: 8px 15px;
    border: none;
    border-radius: 5px;
    font-size: 14px;
    cursor: pointer;
    font-weight: 600;
}

th {
    font-size: 16px;
    color: #000;
    font-weight: 600;
}

.auction-content h2,
.box-heading {
    font-size: 22px;
    color: #5D4037;
}

td {
    font-size: 15px;
}

.send-mail button {
    background: #a35c2e !important;
    border: none;
    color: #fff;
    border-radius: 5px;
    padding: 8px 30px;
    cursor: pointer;
    font-weight: 600;
    width: 100%;
    margin-top: 15px;
}

.bg-yellow {
    background-color: #f4e48f;
}

.bg-dark-yellow {
    background-color: #fff3ce;
}

.bg-light-color {
    background-color: #f7f7f9;
}

label,
.form-label {
    font-size: 16px;
    color: #000;
}

input[type="radio" i] {
    width: 25px;
    height: 15px;
}

.add-btn {
    position: absolute;
    top: 23px;
}

.input,
input {
    width: 100%;
    padding: 0.5rem;
    border: 1px solid #ccc;
    border-radius: 5px;
}


/* Modal Styles */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1050;
}

.modal-container {
    background: white;
    padding: 20px;
    border-radius: 8px;
    width: 400px;
    max-width: 90%;
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.modal-body {
    margin-top: 10px;
}

.modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    border-top: none !important;
}

.close-button {
    background: transparent;
    border: none;
    font-size: 24px;
    cursor: pointer;
}

.chargeDesign h5 {
    font-size: 13px;
}



.chargeDesign input {
    padding: 5px 10px;
    /* 200px: !; */
    width: 138px;
    border-radius: 5px;
}
</style>
